import { useEffect, useMemo, useState, useCallback, useRef, Fragment } from "react";
import * as XLSX from "xlsx";
import jwtDecode from "jwt-decode";
import auctionService from "../../services/auction/auction.service";
import userService from "../../services/user.service";
import { errorToast } from "../../react-toastfiy/toast";

/**
 * New live-auction dashboard — State ▸ Financier ▸ Asset Segment.
 * Talks to:
 *   - /auctions/get-live-dashboard               (aggregation + byDate + subs/nonactive + end_dates)
 *   - /auctions/get-live-dashboard-asset-detail  (bidded assets in a group)
 *   - /auctions/get-live-dashboard-bidders       (distinct bidders: name · phone · #assets)
 * Metrics fold offline (is_offline) winners into bidded/bids/bidders. Bid→RP = Σ bid ÷ Σ reserve
 * (Asset.recommended_bid). Non-active = state+segment-permission users who did not bid that segment.
 */

const SMART_FILTERS_ALLOWED_ROLES = [
  "Auction Super Admin", "Developer", "Super Admin", "Auction CEO", "Business Analyst",
];
// Display order preference only — segments are read from the data, never whitelisted against
// this list, so a new super segment still shows up (it just sorts to the end).
const SEG_ORDER = ["2W", "3W", "4W", "Tractor", "CV", "HCV", "OTHERS", "None"];
const segRank = (s) => {
  const i = SEG_ORDER.indexOf(s);
  return i === -1 ? SEG_ORDER.length : i;
};
const bySeg = (a, b) => segRank(a) - segRank(b) || String(a).localeCompare(String(b));
// A "top winner" holds at least this many assets in the current scope. Mirrors
// _LD_WIN_MIN in auction/views.py — change both together.
const WIN_MIN = 7;

const SEG_COLOR = {
  "2W": "#2f74e6", "3W": "#7c5cf0", "4W": "#0891b2", Tractor: "#15924a", Farm: "#15924a",
  CV: "#dd8a00", HCV: "#e0483d", OTHERS: "#6b7280", None: "#8592a6",
};
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const inr = (n) => Number(n || 0).toLocaleString("en-IN");
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const band = (p) => (p >= 100 ? "good" : p >= 70 ? "mid" : "low");
const fmtDate = (iso) => {
  const p = String(iso || "").split("-");
  return p.length === 3 ? `${+p[2]} ${MON[+p[1] - 1]} ${p[0]}` : iso;
};
const cur = (n) => {
  n = Number(n || 0);
  if (n >= 1e7) return "₹" + (n / 1e7).toFixed(2) + "Cr";
  if (n >= 1e5) return "₹" + (n / 1e5).toFixed(2) + "L";
  if (n >= 1e3) return "₹" + (n / 1e3).toFixed(0) + "k";
  return n ? "₹" + Math.round(n) : "—";
};
function todayISO() { return new Date().toISOString().slice(0, 10); }

// checkbox multi-select dropdown; empty selection == "All". `render` formats each option's label.
function MultiSelect({ label, options, selected, onChange, render }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const summary = selected.length === 0 ? "All"
    : selected.length === 1 ? (render ? render(selected[0]) : selected[0])
    : selected.length + " selected";
  return (
    <div className="nad-fx nad-ms" ref={ref}>
      <span>{label}</span>
      <button type="button" className="nad-ms-btn" onClick={() => setOpen((o) => !o)}>
        <span className="nad-ms-lbl">{summary}</span><span className="nad-ms-car">▾</span>
      </button>
      {open && (
        <div className="nad-ms-pop">
          <div className="nad-ms-actions">
            <button type="button" onClick={() => onChange(options.slice())}>Select all</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="nad-ms-list">
            {options.length === 0 && <div className="nad-mut" style={{ padding: "6px 8px" }}>No options</div>}
            {options.map((o) => (
              <label key={o} className="nad-ms-opt">
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span>{render ? render(o) : o}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const emptyBoard = { states: [], subs: {}, nonactive: {}, state_subs: {}, end_dates: [], byDate: {}, byDateUsers: {},
                     new_bidders: [], ta_bidders: [],
                     owners: [], byDateOwner: {}, byDateOwnerUsers: {}, owner_of: {},
                     financiers: [], byDateFin: {}, byDateFinUsers: {} };

export default function NewAuctionDashboard({ mode = "live", onBack }) {
  const isLive = mode === "live";
  const [board, setBoard] = useState(emptyBoard);
  const [loading, setLoading] = useState(false);
  // Completed mode opens on today only (both bounds) — widen the range from the pickers.
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [F, setF] = useState({ state: [], segment: [], financier: [], endDate: [], owner: [] });
  const [collapsed, setCollapsed] = useState({});
  const [smartOn, setSmartOn] = useState(false);
  // Top level of the accordion: State ▸ Financier ▸ Segment, or Owner ▸ State ▸ Segment.
  // The API emits both trees in the same shape, so switching is a swap, not a re-render path.
  const [groupBy, setGroupBy] = useState("state");
  const [stateHeadStates, setStateHeadStates] = useState([]);
  const [detail, setDetail] = useState(null); // {title, seg, rows, loading}
  const [bidders, setBidders] = useState(null); // {title, rows, loading}
  const [segBreak, setSegBreak] = useState(null); // null | "value" | "rp80" | "meta"
  const [highBids, setHighBids] = useState(null); // {rows, loading} — bids >= ₹15L
  const [topWin, setTopWin] = useState(null);  // {rows, loading} — winners with >= WIN_MIN assets
  const [winOpen, setWinOpen] = useState({});  // which winner rows are expanded
  const [oneBid, setOneBid] = useState(null);  // {by} — single-bid breakdown modal
  const [newU, setNewU] = useState(null);     // {rows, loading, by} — new-user participation
  const [xMenu, setXMenu] = useState(false);   // export split-button menu
  const [wizOpen, setWizOpen] = useState(false);
  const [wizBusy, setWizBusy] = useState(false);
  useEffect(() => {
    if (!xMenu) return;
    const h = () => setXMenu(false);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [xMenu]);

  const currentUser = useMemo(() => {
    try { return jwtDecode(localStorage.getItem("refreshToken")); } catch (e) { return {}; }
  }, []);
  const canUseSmartFilters = SMART_FILTERS_ALLOWED_ROLES.includes(currentUser?.role);

  useEffect(() => {
    if (!canUseSmartFilters || !currentUser?.phone) return;
    const fd = new FormData();
    fd.append("phone", currentUser.phone);
    userService.GetStatesWithStateHeads(fd)
      .then((res) => { if (res?.status === 200) setStateHeadStates(res?.data || []); })
      .catch(() => {});
  }, [canUseSmartFilters, currentUser]);

  const drillScope = useCallback((fd) => {
    fd.append("auction_status", isLive ? "live" : "completed");
    if (!isLive) { fd.append("start_date", start); fd.append("end_date", end); }
    return fd;
  }, [isLive, start, end]);

  const fetchBoard = useCallback(() => {
    setLoading(true);
    auctionService.GetLiveDashboard(drillScope(new FormData()))
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) setBoard({ ...emptyBoard, ...res });
        else { setBoard(emptyBoard); if (res?.msg) errorToast(String(res.msg).slice(0, 200)); }
      })
      .catch((e) => { setLoading(false); setBoard(emptyBoard); errorToast(e?.message || "Failed to load"); });
  }, [drillScope]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const subOf = (st, sg) => board.subs[st + "|" + sg] || 0;
  const naOf = (st, sg) => board.nonactive[st + "|" + sg] || 0;
  // Which tree + date maps the current grouping reads. Owner mode uses the parallel
  // owner ▸ state ▸ segment tree, keyed the same way, so everything downstream is unchanged.
  const isOwner = groupBy === "owner";
  const isFin = groupBy === "financier";
  // Kept only for labels/headers; all data now comes from board.cells via the pivot below.

  // ---- one pivot builder for every grouping -------------------------------------
  // The API returns flat leaf cells carrying ALL FOUR dimensions (state, financier,
  // segment, owner). Every grouping is a pivot of those cells, and because each cell
  // knows all four, EVERY filter works in EVERY grouping — filtering the SH view by
  // financier narrows exactly the cells the state view would.
  const GROUP_KEYS = {
    state:     { outer: "st",  mid: "fin" },
    financier: { outer: "fin", mid: "st" },
    owner:     { outer: "own", mid: "st" },
  };
  // byDate row layout, mirroring the backend's comment.
  // Positional — mirrors the byd row built in auction/views.py. Append only.
  const DATE_FIELDS = [
    "assets", "bidded", "total_bids", "sum_high", "sum_rp", "sum_high_rp", "rp80",
    "sum_high_80", "sum_rp_80", "meta_win", "offline", "rp80_meta_win",
    "sum_high_80_meta", "sum_high_meta", "bid15", "bid1",
    "bid1_high", "bid1_rp",
  ];
  const SUM_FIELDS = [
    "assets", "bidded", "total_bids", "sum_high", "sum_high_rp", "sum_rp", "rp80",
    "sum_high_80", "sum_rp_80", "meta_win", "offline", "rp80_meta_win",
    "sum_high_80_meta", "sum_high_meta", "bid15", "bid1",
    "bid1_high", "bid1_rp",
  ];

  const view = useMemo(() => {
    const { outer, mid } = GROUP_KEYS[groupBy] || GROUP_KEYS.state;
    const cells = board.cells || [];
    const dates = F.endDate;
    const tree = new Map();

    cells.forEach((c) => {
      // Filters apply to the cell's own dimensions — position in the pivot is irrelevant.
      if (F.state.length && !F.state.includes(c.st)) return;
      if (F.financier.length && !F.financier.includes(c.fin)) return;
      if (F.segment.length && !F.segment.includes(c.seg)) return;
      if (F.owner.length && !F.owner.includes(c.own)) return;

      // Date filter: rebuild this cell's metrics from just the selected end dates.
      let m, bidders, wins;
      if (dates.length) {
        m = {}; SUM_FIELDS.forEach((k) => (m[k] = 0));
        dates.forEach((iso) => {
          const r = c.byDate?.[iso];
          if (r) DATE_FIELDS.forEach((k, i) => (m[k] += r[i] || 0));
        });
        if (!m.assets) return;                       // nothing on those dates
        const set = new Set();
        dates.forEach((iso) => (c.byDateUsers?.[iso] || []).forEach((id) => set.add(id)));
        bidders = [...set];
        wins = [];
        dates.forEach((iso) => (c.byDateWinners?.[iso] || []).forEach((w) => wins.push(w)));
      } else {
        m = {}; SUM_FIELDS.forEach((k) => (m[k] = c[k] || 0));
        bidders = c.bidders || [];
        wins = c.winners || [];
      }

      const ok = c[outer], mk = c[mid];
      let g = tree.get(ok);
      if (!g) { g = new Map(); tree.set(ok, g); }
      let sub = g.get(mk);
      if (!sub) { sub = new Map(); g.set(mk, sub); }
      // Several cells can collapse into one pivot row (e.g. the SH view merges every
      // financier), so metrics are summed and bidder ids unioned, never replaced.
      let row = sub.get(c.seg);
      if (!row) {
        row = { seg: c.seg, _b: new Set(), _w: new Map() };
        SUM_FIELDS.forEach((k) => (row[k] = 0));
        sub.set(c.seg, row);
      }
      SUM_FIELDS.forEach((k) => (row[k] += m[k] || 0));
      bidders.forEach((id) => row._b.add(id));
      // Each asset belongs to exactly one cell, so win counts SUM across cells.
      // Each entry is [user, wins, wins that drew only one bid].
      wins.forEach(([id, n, one]) => {
        const e = row._w.get(id) || [0, 0];
        e[0] += n; e[1] += one || 0;
        row._w.set(id, e);
      });
    });

    return [...tree.entries()]
      .map(([ok, g]) => ({
        state: ok,
        fins: [...g.entries()]
          .map(([mk, sub]) => ({
            fin: mk,
            segs: [...sub.values()]
              .map((r) => {
                const { _b, ...rest } = r;
                return { ...rest, bidders: [..._b], pbidders: _b.size };
              })
              .sort((a, b) => b.assets - a.assets),
          }))
          .sort((a, b) => b.segs.reduce((x, y) => x + y.assets, 0) - a.segs.reduce((x, y) => x + y.assets, 0)),
      }))
      // Unassigned is a gap to fill, not an owner — keep it last however big it is.
      .sort((a, b) => (a.state === "Unassigned") - (b.state === "Unassigned")
        || b.fins.reduce((x, f) => x + f.segs.reduce((y, s) => y + s.assets, 0), 0)
         - a.fins.reduce((x, f) => x + f.segs.reduce((y, s) => y + s.assets, 0), 0));
  }, [board.cells, groupBy, F]);

  const stot = (st) => {
    const t = { assets: 0, bidded: 0, pbidders: 0, total_bids: 0, sum_high: 0, sum_high_rp: 0, sum_rp: 0, rp80: 0, sum_high_80: 0, sum_rp_80: 0, meta_win: 0, offline: 0, rp80_meta_win: 0, sum_high_80_meta: 0, sum_high_meta: 0, bid15: 0 };
    // Unique bidders must be a UNION, not a sum: one bidder active in two segments (or two
    // financiers) would otherwise be counted twice — 190 vs a true 131 on the live board.
    const u = new Set();
    st.fins.forEach((f) => f.segs.forEach((s) => {
      Object.keys(t).forEach((k) => (t[k] += s[k] || 0));
      (s.bidders || []).forEach((id) => u.add(id));
    }));
    t.pbidders = u.size;
    // Per-segment listed/bidded for the collapsed row's chips, so the product mix is
    // readable without expanding. Ordered by listed count, biggest first.
    const bySeg = new Map();
    st.fins.forEach((f) => f.segs.forEach((s) => {
      const e = bySeg.get(s.seg) || { seg: s.seg, assets: 0, bidded: 0 };
      e.assets += s.assets || 0;
      e.bidded += s.bidded || 0;
      bySeg.set(s.seg, e);
    }));
    t.segChips = [...bySeg.values()].filter((e) => e.assets).sort((a, b) => b.assets - a.assets);
    return t;
  };

  // Options for one filter are derived from the board with every OTHER filter already
  // applied (`skip` is the dimension being built), so a dropdown never offers a value that
  // would return nothing. Its own selection is skipped, or picking one value would
  // immediately hide the rest and make the filter un-editable.
  const collect = useCallback((skip) => {
    const out = { state: new Set(), financier: new Set(), segment: new Set(), endDate: new Set(), owner: new Set() };
    (board.cells || []).forEach((c) => {
      // Options come off the cells' own dimensions, so they are the same in every
      // grouping — a filter never lists values from a level it does not mean.
      if (skip !== "state" && F.state.length && !F.state.includes(c.st)) return;
      if (skip !== "financier" && F.financier.length && !F.financier.includes(c.fin)) return;
      if (skip !== "segment" && F.segment.length && !F.segment.includes(c.seg)) return;
      if (skip !== "owner" && F.owner.length && !F.owner.includes(c.own)) return;
      const dates = Object.keys(c.byDate || {}).filter(
        (d) => d !== "NA" && (c.byDate[d]?.[0] || 0) > 0,
      );
      if (skip !== "endDate" && F.endDate.length && !dates.some((d) => F.endDate.includes(d))) return;
      out.state.add(c.st);
      out.financier.add(c.fin);
      out.segment.add(c.seg);
      out.owner.add(c.own);
      dates.forEach((d) => out.endDate.add(d));
    });
    return out;
  }, [board.cells, F]);

  // Alphabetical everywhere it means anything; end dates stay chronological.
  const stateOpts = useMemo(() => [...collect("state").state].sort(), [collect]);
  const finOpts = useMemo(() => [...collect("financier").financier].sort(), [collect]);
  const segOpts = useMemo(() => [...collect("segment").segment].sort(), [collect]);
  const dateOpts = useMemo(() => [...collect("endDate").endDate].sort(), [collect]);
  // Unassigned last, matching the owner grouping's own ordering.
  const ownerOpts = useMemo(
    () =>
      [...collect("owner").owner].sort(
        (a, b) =>
          (a === "Unassigned") - (b === "Unassigned") || a.localeCompare(b),
      ),
    [collect],
  );

  const grand = useMemo(() => {
    const g = { assets: 0, bidded: 0, total_bids: 0, sum_high: 0, sum_high_rp: 0, sum_rp: 0, rp80: 0, sum_high_80: 0, sum_rp_80: 0, meta_win: 0, offline: 0, rp80_meta_win: 0, sum_high_80_meta: 0, sum_high_meta: 0, bid15: 0, bid1: 0, bid1_high: 0, bid1_rp: 0 };
    const u = new Set();
    const w = new Map();
    view.forEach((st) => st.fins.forEach((f) => f.segs.forEach((s) => {
      Object.keys(g).forEach((k) => (g[k] += s[k] || 0));
      (s.bidders || []).forEach((id) => u.add(id));
      (s._w || new Map()).forEach((v, id) => {
        const e = w.get(id) || [0, 0];
        e[0] += v[0]; e[1] += v[1];
        w.set(id, e);
      });
    })));
    // Deduped across every state/financier/segment currently in view.
    g.pbidders = u.size;
    // Winners holding WIN_MIN or more assets in what is currently in view. A winner is the
    // recorded winner once an auction closes, or whoever currently holds the highest bid
    // while it runs — so the figure means something on both boards.
    // Recently-added users among the bidders in view. The payload sends only ids that are
    // both new and bidding, so intersecting with the filtered union keeps the tile exact.
    const fresh = new Set(board.new_bidders || []);
    const taSet = new Set(board.ta_bidders || []);
    g.new_bidders = 0;
    g.ta_bidders = 0;
    u.forEach((id) => {
      if (fresh.has(id)) g.new_bidders += 1;
      if (taSet.has(id)) g.ta_bidders += 1;
    });
    g.top_winners = 0; g.top_winner_assets = 0; g.winners = w.size; g.top_winner_max = 0;
    g.top_winner_single = 0;
    w.forEach((v) => {
      if (v[0] > g.top_winner_max) g.top_winner_max = v[0];
      if (v[0] >= WIN_MIN) {
        g.top_winners += 1; g.top_winner_assets += v[0]; g.top_winner_single += v[1];
      }
    });
    return g;
  }, [view, board.new_bidders, board.ta_bidders]);

  // Subscribers across the states currently in view — the denominator for participation.
  // state_subs is keyed by the SUBSCRIBER's own state, while the board groups by the
  // AUCTION's state, so a bidder can participate outside their home state; participation
  // is therefore a rate against the local subscriber base, not a capped share of it.
  const subsInView = useMemo(
    () => view.reduce((n, st) => n + (board.state_subs[st.state] || 0), 0),
    [view, board.state_subs],
  );

  // Bid→RP rolled up by segment across the CURRENT filtered view (same source as the KPI tiles).
  const segRows = useMemo(() => {
    const m = {};
    view.forEach((st) => st.fins.forEach((f) => f.segs.forEach((s) => {
      const g = m[s.seg] || (m[s.seg] = { seg: s.seg, assets: 0, bidded: 0, rp80: 0, sum_high: 0, sum_high_rp: 0, sum_rp: 0, sum_high_80: 0, sum_rp_80: 0, rp80_meta_win: 0, meta_win: 0, offline: 0, sum_high_80_meta: 0, sum_high_meta: 0 });
      g.assets += s.assets || 0; g.bidded += s.bidded || 0; g.rp80 += s.rp80 || 0;
      g.sum_high += s.sum_high || 0; g.sum_high_rp += s.sum_high_rp || 0; g.sum_rp += s.sum_rp || 0;
      g.sum_high_80 += s.sum_high_80 || 0; g.sum_rp_80 += s.sum_rp_80 || 0;
      g.rp80_meta_win += s.rp80_meta_win || 0;
      g.meta_win += s.meta_win || 0; g.offline += s.offline || 0;
      g.sum_high_80_meta += s.sum_high_80_meta || 0; g.sum_high_meta += s.sum_high_meta || 0;
    })));
    return Object.values(m).sort((a, b) => bySeg(a.seg, b.seg));
  }, [view]);
  // Only segments that actually have assets above 80% — the modal is about that cohort.
  const rp80Rows = useMemo(() => segRows.filter((r) => r.rp80 > 0), [segRows]);
  // Same idea for the meta-winning drill: segments with at least one meta-winning asset.
  const metaRows = useMemo(() => segRows.filter((r) => r.meta_win > 0), [segRows]);
  // …and for the total-bid-value drill: segments that actually carry bid value.
  const valueRows = useMemo(() => segRows.filter((r) => r.sum_high > 0), [segRows]);

  const allCollapsed = view.length > 0 && view.every((g) => collapsed[g.state]);
  const toggleAll = () => setCollapsed(allCollapsed ? {} : Object.fromEntries(view.map((g) => [g.state, true])));
  const clearFilters = () => {
    setSmartOn(false);
    setF({ state: [], segment: [], financier: [], endDate: [], owner: [] });
  };

  // ---- drill-downs ----
  // A row's outer/mid labels mean different things per grouping, so translate them back
  // into real dimensions before asking the API. In the SH pivot a row spans every
  // financier, so no financier is sent at all.
  const rowDims = (outerLabel, midLabel) => {
    if (groupBy === "owner") return { state: midLabel, financier: "", owner: outerLabel };
    if (groupBy === "financier") return { state: midLabel, financier: outerLabel, owner: "" };
    return { state: outerLabel, financier: midLabel, owner: "" };
  };

  const openDetail = (st, fin, s) => {
    setDetail({ title: `${st} · ${fin} · ${s.seg}`, seg: s, rows: [], loading: true });
    const fd = drillScope(new FormData());
    const d = rowDims(st, fin);
    fd.append("state", d.state); fd.append("financier", d.financier);
    fd.append("owner", d.owner); fd.append("segment", s.seg);
    auctionService.GetLiveDashboardAssetDetail(fd)
      .then((res) => setDetail((d) => (d ? { ...d, loading: false, rows: res?.status === 200 ? res.data || [] : [] } : d)))
      .catch(() => setDetail((d) => (d ? { ...d, loading: false } : d)));
  };
  const openHighBids = () => {
    setHighBids({ rows: [], loading: true });
    auctionService.GetLiveDashboardHighBids(drillScope(new FormData()))
      .then((res) => setHighBids((h) => (h ? { ...h, loading: false, rows: res?.status === 200 ? res.data || [] : [] } : h)))
      .catch(() => setHighBids((h) => (h ? { ...h, loading: false } : h)));
  };
  const kpiOpen = (what) => {
    if (what === "high") return openHighBids();
    if (what === "winners") return openTopWinners();
    if (what === "onebid") return setOneBid({ by: "state" });
    if (what === "newusers") return openNewUsers();
    return setSegBreak(what);
  };
  const openTopWinners = () => {
    setTopWin({ rows: [], loading: true });
    setWinOpen({});
    const fd = drillScope(new FormData());
    fd.append("min_assets", String(WIN_MIN));
    auctionService.GetLiveDashboardTopWinners(fd)
      .then((res) => setTopWin((t) => (t ? { ...t, loading: false, rows: res?.status === 200 ? res.data || [] : [] } : t)))
      .catch(() => setTopWin((t) => (t ? { ...t, loading: false } : t)));
  };
  const openNewUsers = () => {
    setNewU({ rows: [], loading: true, by: "user" });
    auctionService.GetLiveDashboardNewUsers(drillScope(new FormData()))
      .then((res) => setNewU((t) => (t ? { ...t, loading: false, rows: res?.status === 200 ? res.data || [] : [],
        since: res?.since, intake: res?.intake } : t)))
      .catch(() => setNewU((t) => (t ? { ...t, loading: false } : t)));
  };
  // State-wise roll-up of the same rows, by the user's HOME state — the question is where
  // the new users are, not where the auctions were.
  const newUserStates = useMemo(() => {
    if (!newU) return [];
    const m = new Map();
    (newU.rows || []).forEach((r) => {
      const k = r.state || "Unknown";
      const e = m.get(k) || { state: k, users: 0, bid: 0, won: 0, one: 0, value: 0 };
      e.users += 1; e.bid += r.assets_bid; e.won += r.assets_won;
      e.one += r.single_bid_wins; e.value += r.value;
      m.set(k, e);
    });
    return [...m.values()].sort((a, b) => b.users - a.users);
  }, [newU]);
  const exportNewUsers = () => newU && dlBook("new_user_participation", [
    { name: "By State", rows: newUserStates.map((r) => ({
      "Home State": r.state, "New Users Bidding": r.users, "Assets Bid": r.bid,
      "Assets Won": r.won, "Uncontested Wins": r.one, "Winning Value": r.value })) },
    { name: "Users", rows: (newU.rows || []).map((r) => ({
      "User": r.name, "Phone": r.phone, "Home State": r.state, "First Subscribed": r.added_on,
      "Assets Bid": r.assets_bid, "Assets Won": r.assets_won,
      "Uncontested Wins": r.single_bid_wins, "% Uncontested": r.single_bid_pct,
      "Winning Value": r.value, "Avg per Asset": r.avg_value,
      "Auctions": r.auctions, "Financiers": r.financiers,
      "Segments": r.segments, "Bid In": r.states, "States": r.states_count,
      "Financier Names": r.financier_list, "Auction IDs": r.auction_list })) },
  ]);

  // Single-bid breakdown, grouped the way the modal asks. Built from board.cells, which
  // the browser already holds, so switching State / Financier / both costs no request.
  const oneBidRows = useMemo(() => {
    if (!oneBid) return [];
    const KEY = {
      state: (c) => c.st,
      financier: (c) => c.fin,
      both: (c) => c.st + "\u0000" + c.fin,
    }[oneBid.by] || ((c) => c.st);
    const dates = F.endDate;
    const m = new Map();
    (board.cells || []).forEach((c) => {
      if (F.state.length && !F.state.includes(c.st)) return;
      if (F.financier.length && !F.financier.includes(c.fin)) return;
      if (F.segment.length && !F.segment.includes(c.seg)) return;
      if (F.owner.length && !F.owner.includes(c.own)) return;
      let one = c.bid1 || 0, bid = c.bidded || 0, all = c.assets || 0;
      let oh = c.bid1_high || 0, orp = c.bid1_rp || 0;
      if (dates.length) {
        one = 0; bid = 0; all = 0; oh = 0; orp = 0;
        dates.forEach((iso) => {
          const r = c.byDate?.[iso];
          if (r) {
            all += r[0] || 0; bid += r[1] || 0; one += r[15] || 0;
            oh += r[16] || 0; orp += r[17] || 0;
          }
        });
      }
      if (!all) return;
      const k = KEY(c);
      const e = m.get(k) || { k, one: 0, bidded: 0, assets: 0, oh: 0, orp: 0 };
      e.one += one; e.bidded += bid; e.assets += all; e.oh += oh; e.orp += orp;
      m.set(k, e);
    });
    const out = [...m.values()].filter((e) => e.one);
    return out
      .map((e) => {
        const p = e.k.split("\u0000");
        return {
          ...e, state: p[0], financier: p.length > 1 ? p[1] : "",
          // Bid against reserve for these assets only — the board's Bid→RP covers every
          // bidded asset, which is a different (and much healthier) population.
          rp_pct: e.orp ? Math.round((e.oh / e.orp) * 100) : null,
        };
      })
      .sort((a, b) => b.one - a.one);
  }, [oneBid, board.cells, F]);

  const exportOneBid = () => {
    if (!oneBid || oneBid.busy) return;
    setOneBid((o) => ({ ...o, busy: true }));
    const label = { state: "State", financier: "Financier", both: "State / Financier" }[oneBid.by];
    const summary = oneBidRows.map((r) => ({
      [oneBid.by === "financier" ? "Financier" : "State"]: r.state,
      ...(oneBid.by === "both" ? { Financier: r.financier } : {}),
      "Single-bid Assets": r.one, "Bidded": r.bidded, "Listed": r.assets,
      "% of Bidded": r.bidded ? +((r.one / r.bidded) * 100).toFixed(1) : 0,
      "Sum Bid": r.oh, "Sum Reserve": r.orp,
      "RP to Bid %": r.orp ? +((r.oh / r.orp) * 100).toFixed(1) : "",
    }));
    auctionService.GetLiveDashboardSingleBid(drillScope(new FormData()))
      .then((res) => {
        const rows = res?.status === 200 ? res.data || [] : [];
        dlBook("single_bid_assets", [
          { name: "Summary by " + label.replace(/\W+/g, " ").trim(), rows: summary },
          {
            name: "Assets",
            rows: rows.map((r) => ({
              "Auction ID": r.auction_id, "State": r.state, "Financier": r.financier,
              "Segment": r.segment, "SH / Owner": r.owner,
              "Reference No": r.reference_no, "Reg No": r.reg_no, "Make Model": r.make_model,
              "Mfg Year": r.mfg_yr, "Yard": r.yard,
              "Start Price": r.start_price, "Reserve (RP)": r.reserve, "Bid": r.bid,
              "Bid to RP %": r.bid_to_rp, "Bidder": r.bidder, "Bidder Phone": r.bidder_phone,
              "Mode": r.mode, "Auction End": r.end_date,
            })),
          },
        ]);
      })
      .catch(() => errorToast("Export failed."))
      .finally(() => setOneBid((o) => (o ? { ...o, busy: false } : o)));
  };

  // Two sheets: one row per winner, and one row per asset they won. The per-asset rows
  // are fetched only now — ~9k rows on a full month, which the popup itself never needs.
  const exportTopWinners = () => {
    if (!topWin || topWin.busy) return;
    setTopWin((t) => ({ ...t, busy: true }));
    const fd = drillScope(new FormData());
    fd.append("min_assets", String(WIN_MIN));
    fd.append("detail", "1");
    auctionService.GetLiveDashboardTopWinners(fd)
      .then((res) => {
        const rows = res?.status === 200 ? res.data || [] : topWin.rows;
        const detail = res?.status === 200 ? res.detail || [] : [];
        dlBook("winners_" + WIN_MIN + "_plus", [
          {
            name: "Winners",
            rows: rows.map((r) => ({
              "Winner": r.name, "Phone": r.phone, "Home State": r.state,
              "Assets Won": r.assets_won,
              "Uncontested Wins": r.single_bid_wins, "% Uncontested": r.single_bid_pct,
              "Winning Value": r.value, "Avg per Asset": r.avg_value,
              "Auctions": r.auctions, "Financiers": r.financiers,
              "Segments": r.segments, "Won In": r.states, "States": r.states_count,
              "Financier Names": r.financier_list, "Auction IDs": r.auction_list,
            })),
          },
          {
            name: "Assets",
            rows: detail.map((r) => ({
              "Winner": r.winner, "Phone": r.phone, "Auction ID": r.auction_id,
              "Financier": r.financier, "State": r.state, "Segment": r.segment,
              "Reference No": r.reference_no, "Reg No": r.reg_no, "Make Model": r.make_model,
              "Mfg Year": r.mfg_yr, "Yard": r.yard, "Reserve (RP)": r.reserve,
              "Winning Bid": r.amount, "Mode": r.mode,
              "Single Bid": r.single_bid, "Auction End": r.end_date,
            })),
          },
        ]);
      })
      .catch(() => errorToast("Export failed."))
      .finally(() => setTopWin((t) => (t ? { ...t, busy: false } : t)));
  };

  const exportHighBids = () => highBids && dlSheet("bids_above_15L", highBids.rows.map((r) => ({
    Auction: r.auction_id, State: r.state, Segment: r.segment, Financier: r.financier,
    "Asset Reference No": r.reference_no, "Make Model": r.make_model,
    "Start Price": r.start_price, "RP": r.recommended_bid, "Bid Price": r.bid,
    "Bidder Name": r.bidder_name, "Bidder Phone": r.bidder_phone, Mode: r.mode,
  })));

  const openBidders = (title, scope) => {
    setBidders({ title, rows: [], loading: true });
    const fd = drillScope(new FormData());
    Object.entries(scope).forEach(([k, v]) => fd.append(k, v));
    auctionService.GetLiveDashboardBidders(fd)
      .then((res) => setBidders((b) => (b ? { ...b, loading: false, rows: res?.status === 200 ? res.data || [] : [] } : b)))
      .catch(() => setBidders((b) => (b ? { ...b, loading: false } : b)));
  };

  // ---- Excel export ----
  const mainRow = (st, f, s) => ({
    State: st, Financier: f, Segment: s.seg, Assets: s.assets, Bidded: s.bidded, Bidders: s.pbidders,
    "Total Bids": s.total_bids, "Bids/Asset": s.assets ? +(s.total_bids / s.assets).toFixed(2) : 0,
    "Sum Max Bids": Math.round(s.sum_high || 0),
    "Avg Sell": s.bidded ? Math.round(s.sum_high / s.bidded) : 0,
    "Bid to RP %": s.sum_rp ? Math.round((s.sum_high_rp / s.sum_rp) * 100) : "",
    "Assets Bid to RP > 80%": s.rp80 || 0,
    "Meta Winning": s.meta_win || 0,
    "Offline Bidded": s.offline || 0,
    Subs: subOf(st, s.seg), "Non-active": naOf(st, s.seg),
  });
  // Multi-sheet variant of dlSheet, for exports that carry a summary and its detail.
  const dlBook = (name, sheets) => {
    const live = sheets.filter((s) => s.rows && s.rows.length);
    if (!live.length) { errorToast("Nothing to export."); return; }
    const wb = XLSX.utils.book_new();
    live.forEach((s) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(s.rows), s.name.slice(0, 31)));
    XLSX.writeFile(wb, name.replace(/[^\w.-]+/g, "_") + "_" + todayISO() + ".xlsx");
  };
  const dlSheet = (name, rows, sheet = "Data") => {
    if (!rows.length) { errorToast("Nothing to export."); return; }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheet);
    XLSX.writeFile(wb, name.replace(/[^\w.-]+/g, "_") + "_" + todayISO() + ".xlsx");
  };
  const exportMain = () => dlSheet("live_dashboard", view.flatMap((st) => st.fins.flatMap((f) => f.segs.map((s) => mainRow(st.state, f.fin, s)))));

  // Asset-level export. The server streams a finished .xlsx; the browser only saves it.
  // One at a time — a slow export with a live button gets clicked three times, and that
  // really is three times the queries.
  const runAssetExport = (opts) => {
    if (wizBusy) return;
    setWizBusy(true);
    const fd = new FormData();
    fd.append("auction_status", mode);
    fd.append("start_date", start);
    fd.append("end_date", end);
    Object.entries(opts).forEach(([k, v]) => {
      if (Array.isArray(v)) { if (v.length) fd.append(k, JSON.stringify(v)); }
      else fd.append(k, v);
    });
    auctionService.GetLiveDashboardAssetExport(fd)
      .then((res) => {
        const type = res?.data?.type || "";
        if (type.indexOf("json") >= 0) {               // server answered with an error
          return res.data.text().then((t) => {
            let m = "Export failed.";
            try { m = JSON.parse(t).msg || m; } catch (e) { /* keep default */ }
            errorToast(m);
          });
        }
        const cd = res?.headers?.["content-disposition"] || "";
        const hit = /filename="?([^";]+)"?/.exec(cd);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = hit ? hit[1] : "assets_" + todayISO() + ".xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setWizOpen(false);
      })
      .catch(() => errorToast("Export failed."))
      .finally(() => setWizBusy(false));
  };
  const exportState = (st) => dlSheet("live_" + st.state, st.fins.flatMap((f) => f.segs.map((s) => mainRow(st.state, f.fin, s))));
  const exportDetail = () => detail && dlSheet("assets_" + detail.title, detail.rows.map((r) => ({
    Asset: r.name, "Reg No": r.reg_no, Mode: r.is_offline ? "Offline" : "Online",
    "Meta Status": r.meta_status || "", "Reserve (RP)": r.reserve, "Highest Bid": r.bid,
    "Bid to RP %": r.bid_to_rp == null ? "" : r.bid_to_rp, Bidders: r.bidders, Bids: r.bids,
  })));
  const exportValueBreak = () => dlSheet("total_bid_value_by_segment", valueRows.map((r) => ({
    Segment: r.seg,
    Bidded: r.bidded,
    "Total Bid Value": Math.round(r.sum_high),
    "Avg per Bidded Asset": r.bidded ? Math.round(r.sum_high / r.bidded) : 0,
    "Share of Total Value %": pct(r.sum_high, grand.sum_high),
  })));
  const exportMetaBreak = () => dlSheet("meta_winning_by_segment", metaRows.map((r) => ({
    Segment: r.seg,
    "Meta Winning": r.meta_win,
    "Meta Winning Bid Value": Math.round(r.sum_high_meta || 0),
    Bidded: r.bidded,
    "Share of Bidded %": pct(r.meta_win, r.bidded),
    "Of which above 80% Bid to RP": r.rp80_meta_win || 0,
  })));
  const exportSegBreak = () => dlSheet("bid_to_rp_above_80_by_segment", rp80Rows.map((r) => ({
    Segment: r.seg,
    "Assets above 80%": r.rp80,
    "Meta Winning (above 80%)": r.rp80_meta_win || 0,
    "Meta Winning Bid Value (above 80%)": Math.round(r.sum_high_80_meta || 0),
    Bidded: r.bidded,
    "Share of Bidded %": pct(r.rp80, r.bidded),
    "Sum Max Bids (above 80% only)": Math.round(r.sum_high_80),
    "Sum Reserve (above 80% only)": Math.round(r.sum_rp_80),
    "Bid to RP % (above 80% only)": r.sum_rp_80 ? Math.round((r.sum_high_80 / r.sum_rp_80) * 100) : "",
  })));
  const exportBidders = () => bidders && dlSheet("bidders_" + bidders.title, bidders.rows.map((r, i) => ({
    "#": i + 1, Name: r.name, "Assets Bid": r.assets_bid, Phone: r.phone,
  })));

  return (
    <div className="nad">
      <style>{CSS}</style>

      <div className="nad-top">
        <div>
          <div className="nad-title">{isLive ? "Live auctions" : "Completed auctions"} <span className="nad-badge">financier × segment</span></div>
          <div className="nad-sub">State ▸ Financier ▸ Asset segment · offline-inclusive · Bid→RP vs reserve</div>
        </div>
        <div className="nad-ctrls">
          {!isLive && (
            <>
              <label className="nad-fx"><span>From</span><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
              <label className="nad-fx"><span>To</span><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
            </>
          )}
          <div className="nad-xw">
            <div className="nad-xb">
              <button type="button" onClick={exportMain}>⤓ Export Excel</button>
              <button type="button" className="car" title="More export options"
                onClick={(e) => { e.stopPropagation(); setXMenu((v) => !v); }}>▾</button>
            </div>
            {xMenu && (
              <div className="nad-xmenu" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="nad-xmi" onClick={() => { setXMenu(false); exportMain(); }}>
                  <b>Quick — this view<em>0 queries</em></b>
                  <span>The board as it is on screen, with its totals. Written in the browser from data already loaded.</span>
                </button>
                <button type="button" className="nad-xmi" onClick={() => { setXMenu(false); setWizOpen(true); }}>
                  <b>Export wizard…<em className="a">asset level</em></b>
                  <span>One row per asset. Choose scope, columns, row hierarchy and sheet split.</span>
                </button>
              </div>
            )}
          </div>
          <button className="nad-btn" onClick={fetchBoard}>↻ Refresh</button>
          {onBack && <button className="nad-btn nad-primary" onClick={onBack}>← Old view</button>}
        </div>
      </div>

      {/* filters */}
      <div className="nad-filters">
        <div>
          <span className="lbl">Group by</span>
          <div className="nad-groupby">
            <button
              className={groupBy === "state" ? "on" : ""}
              onClick={() => setGroupBy("state")}
            >
              State
            </button>
            <button
              className={groupBy === "financier" ? "on" : ""}
              onClick={() => setGroupBy("financier")}
            >
              Financier
            </button>
            <button
              className={groupBy === "owner" ? "on" : ""}
              onClick={() => {
                setGroupBy("owner");
                // Financier does not exist in the owner tree, and a state picked in the
                // other grouping may not be a valid mid-level value here — drop both
                // rather than leave an invisible filter narrowing the board.
                setF((p2) => ({ ...p2, financier: [] }));
              }}
            >
              SH / Owner
            </button>
          </div>
        </div>
        <MultiSelect label="State" options={stateOpts} selected={F.state} onChange={(v) => setF({ ...F, state: v })} />
        {/* the owner tree has no financier level, so the filter would do nothing there */}
        {/* the owner tree has no financier level, so the filter would do nothing there */}
        {!isOwner && (
          <MultiSelect label="Financier" options={finOpts} selected={F.financier} onChange={(v) => setF({ ...F, financier: v })} />
        )}
        <MultiSelect label="Segment" options={segOpts} selected={F.segment} onChange={(v) => setF({ ...F, segment: v })} />
        <MultiSelect label="SH / Owner" options={ownerOpts} selected={F.owner} onChange={(v) => setF({ ...F, owner: v })} />
        <MultiSelect label="End date" options={dateOpts} render={fmtDate} selected={F.endDate} onChange={(v) => setF({ ...F, endDate: v })} />
        {canUseSmartFilters && (
          <label className="nad-smart" title={stateHeadStates.length ? "Scope to your states: " + stateHeadStates.join(", ") : "No states mapped to you"}>
            <input
              type="checkbox"
              checked={smartOn}
              disabled={!stateHeadStates.length}
              onChange={() => {
                // Push the scoped states into the State filter so the user can SEE what smart
                // mode applied (and edit it); turning it off drops exactly that selection.
                const on = !smartOn;
                setSmartOn(on);
                setF((prev) => ({
                  ...prev,
                  state: on ? [...stateHeadStates].sort() : [],
                }));
              }}
            />
            <span>Smart filters</span>
          </label>
        )}
        <button className="nad-clear" onClick={clearFilters}>Clear</button>
      </div>

      {/* KPIs */}
      <div className="nad-kpis">
        {[
          { l: "States", v: view.length },
          { l: "Listed assets", v: inr(grand.assets) },
          { l: "Bidded", v: inr(grand.bidded), c: `${pct(grand.bidded, grand.assets)}% listed`, hl: 1 },
          { l: "Total bids", v: inr(grand.total_bids) },
          {
            // Deduped across every state/financier/segment in view — a bidder active in
            // several of them counts once. Same union (online bids + offline) as UBP.
            l: "Unique bidders",
            v: inr(grand.pbidders),
            c: subsInView ? `${pct(grand.pbidders, subsInView)}% of ${inr(subsInView)} subs` : "",
          },
          {
            // Σ per-asset highest bid over every bidded asset — the same figure the table's
            // "Sum max bids" column totals.
            l: "Total bid value",
            v: cur(grand.sum_high),
            c: grand.bidded ? `avg ${cur(grand.sum_high / grand.bidded)} / bidded asset` : "",
            hl: 1, click: "value",
          },
          { l: "Bid → RP", v: (grand.sum_rp ? Math.round((grand.sum_high_rp / grand.sum_rp) * 100) : 0), u: "%", hl: 1 },
          {
            l: "Bid→RP above 80%",
            v: inr(grand.rp80),
            // Blended Bid→RP of just that cohort, how many of them are meta-winning, and how
            // much of the bidded pool the cohort is.
            c: `Bid→RP ${grand.sum_rp_80 ? Math.round((grand.sum_high_80 / grand.sum_rp_80) * 100) : 0}% · ${inr(grand.rp80_meta_win)} meta winning · ${pct(grand.rp80, grand.bidded)}% of bidded`,
            hl: 1, click: "rp80",
          },
          {
            l: "Bids ≥ ₹15L",
            v: inr(grand.bid15),
            c: grand.bidded ? `${pct(grand.bid15, grand.bidded)}% of bidded` : "",
            hl: 1, click: "high",
          },
          {
            l: "Meta winning",
            v: inr(grand.meta_win),
            c: `${pct(grand.meta_win, grand.bidded)}% of bidded · ${inr(grand.rp80_meta_win)} above 80%`,
            hl: 1, click: "meta",
          },
          {
            // Winner = recorded winner once an auction closes, else whoever holds the
            // highest bid while it runs. Counts people, not assets.
            l: `Winners ≥ ${WIN_MIN} assets`,
            v: inr(grand.top_winners),
            c: grand.winners
              ? `${inr(grand.top_winner_assets)} assets · ${inr(grand.top_winner_single)} uncontested (${pct(grand.top_winner_single, grand.top_winner_assets)}%) · top ${inr(grand.top_winner_max)}`
              : "",
            hl: 1, click: "winners",
          },
          {
            // Exactly one bid and no second — nobody competed, so the price is whatever
            // the lone bidder offered.
            l: "Single-bid assets",
            v: inr(grand.bid1),
            c: grand.bidded ? `${pct(grand.bid1, grand.bidded)}% of bidded · ${pct(grand.bid1, grand.assets)}% of listed` : "",
            hl: 1, click: "onebid",
          },
          {
            // First-time subscribers who are bidding. New means no subscription before this
            // one — not a recently created account — and is measured against the end of the
            // period on screen, not today.
            l: "New user participation",
            v: inr(grand.new_bidders),
            // % of bidders is filter-aware; the intake is deliberately NOT — it is a
            // user-base number, and the board's filters are on auctions, so there is no
            // honest way to narrow it. Labelled "overall" so it does not read as filtered.
            c: `${pct(grand.new_bidders, grand.pbidders)}% of bidders · ${inr(board.new_users_total || 0)} new subscribers overall`,
            hl: 1, click: "newusers",
          },
          {
            // Trade-advance buyers (UserProxy.is_channel_financed) who bid in what is
            // currently in view.
            l: "TA users bidding",
            v: inr(grand.ta_bidders),
            c: `${pct(grand.ta_bidders, grand.pbidders)}% of bidders · ${inr(board.ta_users_total || 0)} TA users overall`,
            hl: 1,
          },
        ].map((k, i) => (
          <div
            className={"nad-kpi" + (k.hl ? " hl" : "") + (k.click ? " nad-kpi-clk" : "")}
            key={i}
            role={k.click ? "button" : undefined}
            tabIndex={k.click ? 0 : undefined}
            title={k.click ? "View segment-wise breakdown" : undefined}
            onClick={k.click ? () => kpiOpen(k.click) : undefined}
            onKeyDown={k.click ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); kpiOpen(k.click); } } : undefined}
          >
            <div className="nad-kl">{k.l}{k.click ? <span className="nad-view"> ▸ view</span> : null}</div>
            <div className="nad-kv">{k.v}{k.u ? <small>{k.u}</small> : null}</div>
            {k.c ? <div className="nad-kc">{k.c}</div> : null}
          </div>
        ))}
      </div>

      {/* board */}
      <div className="nad-card">
        <div className="nad-ch nad-ch-row">
          <span>Financier × segment {loading ? <span className="nad-mut">· loading…</span> : null}</span>
          {view.length > 0 && <button className="nad-btn nad-btn-sm" onClick={toggleAll}>{allCollapsed ? "▾ Expand all" : "▸ Collapse all"}</button>}
        </div>
        <div className="nad-scroll">
          <table className="nad-tbl">
            {/* With every state collapsed the only rows left are the state summaries, whose
                numbers don't line up with these column labels — so hide the header entirely. */}
            {!allCollapsed && (
              <thead>
                <tr>
                  <th className="frz f1">{isOwner || isFin ? "State" : "Financier"}</th>
                  <th className="frz f2">Segment</th>
                  <th className="num">Assets</th><th className="num">Bidded</th><th className="num">Bidders</th>
                  <th className="num">Total bids</th><th className="num">Bids/ast</th>
                  <th className="num">Sum max bids</th><th className="num">Avg sell</th>
                  <th className="num">Bid→RP</th><th className="num">Meta win</th>
                  <th className="num">Subs</th><th className="num">Non-act</th>
                </tr>
              </thead>
            )}
            <tbody>
              {view.map((st) => {
                const t = stot(st); const isC = collapsed[st.state];
                const segAgg = {};
                st.fins.forEach((f) => f.segs.forEach((s) => { const g = segAgg[s.seg] || (segAgg[s.seg] = { sh: 0, bd: 0, r80: 0 }); g.sh += s.sum_high; g.bd += s.bidded; g.r80 += s.rp80 || 0; }));
                const srp = t.sum_rp ? Math.round((t.sum_high_rp / t.sum_rp) * 100) : 0;
                return (
                  <Fragment key={st.state}>
                    <tr className="nad-gh" onClick={() => setCollapsed((c) => ({ ...c, [st.state]: !c[st.state] }))}>
                      <td colSpan={13}>
                        <div className="nad-gh-in">
                          <div className="nad-gh-lead">
                            <span className="nad-caret">{isC ? "▸" : "▾"}</span>
                            <b className="nad-gh-state">{st.state}</b>
                            <span className="nad-mut">{st.fins.length} financiers · {pct(t.bidded, t.assets)}% bidded</span>
                            <button className="nad-dl-sm" title="Download this state" onClick={(e) => { e.stopPropagation(); exportState(st); }}>⤓</button>
                          </div>
                          <div className="nad-gh-metrics">
                            <span className="nad-gi"><i>Assets</i><b>{inr(t.assets)}</b></span>
                            <span className="nad-gi"><i>Bidded</i><b>{inr(t.bidded)}</b></span>
                            <span className="nad-gi"><i>Total bids</i><b>{inr(t.total_bids)}</b></span>
                            <span className="nad-gi"><i>Unique bidders</i><b>{inr(t.pbidders)}</b></span>
                            <span className="nad-gi"><i>Participation</i><b>{pct(t.pbidders, board.state_subs[st.state] || 0)}%</b></span>
                            <span className="nad-gi"><i>Bids/ast</i><b>{t.bidded ? (t.total_bids / t.bidded).toFixed(2) : 0}</b></span>
                            <span className="nad-gi"><i>Sum max bids</i><b>{cur(t.sum_high)}</b></span>
                            <span className="nad-gi"><i>Bid→RP</i><b className={"g-" + band(srp)}>{srp}%</b></span>
                            <span className="nad-gi"><i>Meta win</i><b>{inr(t.meta_win)}</b></span>
                            {!isOwner && !isFin && (
                              <span className="nad-gi"><i>State subs</i><b>{inr(board.state_subs[st.state] || 0)}</b></span>
                            )}
                          </div>
                        </div>
                        {/* Segment mix for the collapsed row: listed, and how many of those
                            were bid on. Saves expanding the group just to see the split.
                            Shown even for a single segment: the numbers then repeat the group
                            total, but omitting the chip reads as "no segment data" rather than
                            "one segment", and nothing on the row says which segment it is. */}
                        {t.segChips?.length > 0 && (
                          <div className="nad-chips">
                            {t.segChips.map((c) => (
                              <span className="nad-chip" key={c.seg}
                                title={`${c.seg}: ${inr(c.assets)} listed, ${inr(c.bidded)} bidded (${pct(c.bidded, c.assets)}%)`}>
                                <u>{c.seg}</u>
                                <b>{inr(c.assets)}</b>
                                <s>{inr(c.bidded)} bid</s>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Assets whose OWN bid→RP cleared 80% — a per-asset tally, so it is
                            not derivable from the state's summed Bid→RP above. */}
                        <div className="nad-asp">
                          <span className="nad-asp-l">Bid→RP above 80%</span>
                          <span className="nad-asp-i">
                            <b className={t.rp80 ? "g-good" : ""}>{inr(t.rp80)}</b>
                            <span className="nad-mut">
                              &nbsp;of {inr(t.bidded)} bidded{t.bidded ? ` · ${pct(t.rp80, t.bidded)}%` : ""}
                              {t.rp80 ? ` · ${inr(t.rp80_meta_win)} meta winning` : ""}
                            </span>
                          </span>
                          {Object.keys(segAgg).filter((x) => segAgg[x].r80).sort(bySeg).map((x) => (
                            <span className="nad-asp-i" key={x}><i style={{ background: SEG_COLOR[x] || "#8592a6" }} />{x} <b>{inr(segAgg[x].r80)}</b></span>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {!isC && st.fins.map((f) => f.segs.map((s, i) => {
                      const key = st.state + "|" + f.fin + "|" + s.seg;
                      const rp = s.sum_rp ? Math.round((s.sum_high_rp / s.sum_rp) * 100) : null;
                      return (
                        <tr className="nad-row" key={key} onClick={() => (s.bidded ? openDetail(st.state, f.fin, s) : null)}>
                          {i === 0 && <td className="frz f1" rowSpan={f.segs.length}>{f.fin}</td>}
                          <td className="frz f2">
                            <span className="pillseg" style={{ background: SEG_COLOR[s.seg] || "#8592a6" }}>{s.seg}</span>
                            {s.bidded ? <span className="nad-view">▸ view</span> : null}
                          </td>
                          <td className="num b">{s.assets ? inr(s.assets) : "·"}</td>
                          <td className="num b good">{s.bidded ? inr(s.bidded) : "·"}</td>
                          <td className="num">
                            {s.pbidders
                              ? <span className="nad-bd" onClick={(e) => { e.stopPropagation(); openBidders(key, { mode: "group", ...rowDims(st.state, f.fin), segment: s.seg }); }}>{s.pbidders}</span>
                              : "·"}
                          </td>
                          <td className="num">{s.total_bids ? inr(s.total_bids) : "·"}</td>
                          <td className="num">{s.assets ? (s.total_bids / s.assets).toFixed(2) : "·"}</td>
                          <td className="num b">{s.sum_high ? cur(s.sum_high) : "·"}</td>
                          <td className="num">{s.bidded ? cur(s.sum_high / s.bidded) : "·"}</td>
                          <td className="num">{rp == null ? "·" : <span className={"rp g-" + band(rp)}>{rp}%</span>}</td>
                          <td className="num b">{s.meta_win ? inr(s.meta_win) : "·"}</td>
                          <td className="num">{subOf(st.state, s.seg) || "·"}</td>
                          <td className="num">{naOf(st.state, s.seg) || "·"}</td>
                        </tr>
                      );
                    }))}
                  </Fragment>
                );
              })}
              {!loading && view.length === 0 && (
                <tr><td colSpan={13} className="nad-mut" style={{ padding: 20 }}>No rows for this selection.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {wizOpen && (
        <ExportWizard
          isLive={isLive} F={F} cells={board.cells || []}
          stateOpts={stateOpts} finOpts={finOpts} segOpts={segOpts} ownerOpts={ownerOpts}
          busy={wizBusy} onClose={() => (wizBusy ? null : setWizOpen(false))} onRun={runAssetExport}
        />
      )}

      {/* Recently-added users who are bidding (opened from the KPI card) */}
      {newU && (
        <div className="nad-ov" onClick={() => setNewU(null)}>
          <div className="nad-modal nad-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>New user participation</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · {newU.rows.length} bidding{newU.intake ? ` of ${inr(newU.intake)} first-time subscribers` : ""}
                {newU.since ? ` since ${fmtDate(newU.since)}` : ""}
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }}
                onClick={exportNewUsers} disabled={newU.loading}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setNewU(null)}>Close</button>
            </div>
            <div className="nad-ob-ctl">
              <span className="nad-lbl">Show</span>
              <div className="nad-groupby" style={{ marginTop: 0 }}>
                {[["user", "Individual users"], ["state", "By home state"]].map(([k, t]) => (
                  <button key={k} type="button" className={newU.by === k ? "on" : ""}
                    onClick={() => setNewU((o) => ({ ...o, by: k }))}>{t}</button>
                ))}
              </div>
              <span className="nad-mut">
                {newU.by === "state" ? newUserStates.length + " states" : newU.rows.length + " users"}
                {" · the board's filters apply"}
              </span>
            </div>
            {newU.loading ? <div className="nad-mut" style={{ padding: 16 }}>Loading…</div> : (
              <div className="nad-scroll" style={{ maxHeight: "min(64vh,560px)" }}>
                {newU.by === "state" ? (
                  <table className="nad-tbl nad-wtbl">
                    <thead>
                      <tr>
                        <th>Home state</th><th className="num">New users bidding</th>
                        <th className="num">Assets bid</th><th className="num">Assets won</th>
                        <th className="num">Uncontested</th><th className="num">Winning value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newUserStates.map((r, i) => (
                        <tr className="nad-row" key={i}>
                          <td>{r.state}</td>
                          <td className="num b">{inr(r.users)}</td>
                          <td className="num">{inr(r.bid)}</td>
                          <td className="num">{inr(r.won)}</td>
                          <td className="num">{inr(r.one)}</td>
                          <td className="num b">{cur(r.value)}</td>
                        </tr>
                      ))}
                      {!newUserStates.length && (
                        <tr><td colSpan={6} className="nad-mut" style={{ padding: 20 }}>
                          No new users bidding in this selection.
                        </td></tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="nad-tot">
                        <td>Total</td>
                        <td className="num">{inr(newUserStates.reduce((n, r) => n + r.users, 0))}</td>
                        <td className="num">{inr(newUserStates.reduce((n, r) => n + r.bid, 0))}</td>
                        <td className="num">{inr(newUserStates.reduce((n, r) => n + r.won, 0))}</td>
                        <td className="num">{inr(newUserStates.reduce((n, r) => n + r.one, 0))}</td>
                        <td className="num">{cur(newUserStates.reduce((n, r) => n + r.value, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <table className="nad-tbl nad-wtbl">
                    <thead>
                      <tr>
                        <th>User</th><th>Phone</th><th>Home state</th><th>First subscribed</th>
                        <th className="num">Assets bid</th><th className="num">Assets won</th>
                        <th className="num">Uncontested</th><th className="num">% uncontested</th>
                        <th className="num">Winning value</th><th className="num">Auctions</th>
                        <th className="num">Financiers</th><th>Segments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newU.rows.map((r, i) => (
                        <tr className="nad-row" key={i}>
                          <td>{r.name}</td>
                          <td className="mono">{r.phone || "·"}</td>
                          <td>{r.state || "·"}</td>
                          <td className="mono">{r.added_on || "·"}</td>
                          <td className="num b">{inr(r.assets_bid)}</td>
                          <td className="num b">{inr(r.assets_won)}</td>
                          <td className="num">{inr(r.single_bid_wins)}</td>
                          <td className="num">
                            <span className={"rp g-" + (r.single_bid_pct >= 60 ? "low" : r.single_bid_pct >= 30 ? "mid" : "good")}>
                              {r.single_bid_pct}%
                            </span>
                          </td>
                          <td className="num b">{cur(r.value)}</td>
                          <td className="num">{inr(r.auctions)}</td>
                          <td className="num">{inr(r.financiers)}</td>
                          <td className="nad-mut cap" title={r.segments}>{r.segments || "·"}</td>
                        </tr>
                      ))}
                      {!newU.rows.length && (
                        <tr><td colSpan={12} className="nad-mut" style={{ padding: 20 }}>
                          No new users bidding in this selection.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assets that drew exactly one bid (opened from the KPI card) */}
      {oneBid && (
        <div className="nad-ov" onClick={() => setOneBid(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Assets with only one bid</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · {inr(oneBidRows.reduce((n, r) => n + r.one, 0))} of{" "}
                {inr(oneBidRows.reduce((n, r) => n + r.bidded, 0))} bidded
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }}
                onClick={exportOneBid} disabled={oneBid.busy}>
                {oneBid.busy ? "Building…" : "⤓ Excel"}
              </button>
              <button className="nad-btn nad-btn-sm" onClick={() => setOneBid(null)}>Close</button>
            </div>
            <div className="nad-ob-ctl">
              <span className="nad-lbl">Break down by</span>
              <div className="nad-groupby" style={{ marginTop: 0 }}>
                {[["state", "State"], ["financier", "Financier"], ["both", "State × Financier"]].map(([k, t]) => (
                  <button key={k} type="button" className={oneBid.by === k ? "on" : ""}
                    onClick={() => setOneBid((o) => ({ ...o, by: k }))}>{t}</button>
                ))}
              </div>
              <span className="nad-mut">{oneBidRows.length} rows · the board's filters apply</span>
            </div>
            <div className="nad-scroll" style={{ maxHeight: "min(64vh,560px)" }}>
              <table className="nad-tbl nad-wtbl">
                <thead>
                  <tr>
                    <th>{oneBid.by === "financier" ? "Financier" : "State"}</th>
                    {oneBid.by === "both" && <th>Financier</th>}
                    <th className="num">Single-bid</th>
                    <th className="num">Bidded</th><th className="num">Listed</th>
                    <th className="num">% of bidded</th>
                    <th className="num">RP → bid</th>
                  </tr>
                </thead>
                <tbody>
                  {oneBidRows.map((r, i) => (
                    <tr className="nad-row" key={i}>
                      <td>{r.state}</td>
                      {oneBid.by === "both" && <td>{r.financier}</td>}
                      <td className="num b">{inr(r.one)}</td>
                      <td className="num">{inr(r.bidded)}</td>
                      <td className="num">{inr(r.assets)}</td>
                      <td className="num b">{pct(r.one, r.bidded)}%</td>
                      <td className="num">
                        {r.rp_pct == null ? "·" : <span className={"rp g-" + band(r.rp_pct)}>{r.rp_pct}%</span>}
                      </td>
                    </tr>
                  ))}
                  {!oneBidRows.length && (
                    <tr><td colSpan={7} className="nad-mut" style={{ padding: 20 }}>
                      No single-bid assets in this selection.
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="nad-tot">
                    <td>Total</td>
                    {oneBid.by === "both" && <td />}
                    <td className="num">{inr(oneBidRows.reduce((n, r) => n + r.one, 0))}</td>
                    <td className="num">{inr(oneBidRows.reduce((n, r) => n + r.bidded, 0))}</td>
                    <td className="num">{inr(oneBidRows.reduce((n, r) => n + r.assets, 0))}</td>
                    <td className="num">{pct(oneBidRows.reduce((n, r) => n + r.one, 0), oneBidRows.reduce((n, r) => n + r.bidded, 0))}%</td>
                    <td className="num">{pct(oneBidRows.reduce((n, r) => n + r.oh, 0), oneBidRows.reduce((n, r) => n + r.orp, 0))}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Winners holding WIN_MIN+ assets (opened from the KPI card) */}
      {topWin && (
        <div className="nad-ov" onClick={() => setTopWin(null)}>
          <div className="nad-modal nad-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Winners with {WIN_MIN} or more assets</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · {topWin.rows.length} winner{topWin.rows.length === 1 ? "" : "s"}
                {topWin.rows.length
                  ? ` · ${inr(topWin.rows.reduce((n, r) => n + r.assets_won, 0))} assets · ${inr(topWin.rows.reduce((n, r) => n + r.single_bid_wins, 0))} uncontested · ${cur(topWin.rows.reduce((n, r) => n + r.value, 0))}`
                  : ""}
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }}
                onClick={exportTopWinners} disabled={topWin.busy || topWin.loading}>
                {topWin.busy ? "Building…" : "⤓ Excel"}
              </button>
              <button className="nad-btn nad-btn-sm" onClick={() => setTopWin(null)}>Close</button>
            </div>
            {topWin.loading ? <div className="nad-mut" style={{ padding: 16 }}>Loading…</div> : (
              <div className="nad-scroll" style={{ maxHeight: "min(70vh,620px)" }}>
                <table className="nad-tbl nad-wtbl">
                  <thead>
                    <tr>
                      <th>Winner</th><th>Phone</th><th>Home state</th>
                      <th className="num">Assets won</th>
                      <th className="num">Uncontested</th><th className="num">% uncontested</th>
                      <th className="num">Winning value</th>
                      <th className="num">Avg / asset</th><th className="num">Auctions</th>
                      <th className="num">Financiers</th><th>Segments</th><th>Won in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topWin.rows.map((r, i) => (
                      <Fragment key={i}>
                      <tr className="nad-row nad-win-r" onClick={() => setWinOpen((o) => ({ ...o, [i]: !o[i] }))}>
                        <td><span className="nad-caret">{winOpen[i] ? "▾" : "▸"}</span>{r.name}</td>
                        <td className="mono">{r.phone || "·"}</td>
                        <td>{r.state || "·"}</td>
                        <td className="num b">{inr(r.assets_won)}</td>
                        <td className="num">{inr(r.single_bid_wins)}</td>
                        <td className="num">
                          <span className={"rp g-" + (r.single_bid_pct >= 60 ? "low" : r.single_bid_pct >= 30 ? "mid" : "good")}>
                            {r.single_bid_pct}%
                          </span>
                        </td>
                        <td className="num b">{cur(r.value)}</td>
                        <td className="num">{cur(r.avg_value)}</td>
                        <td className="num">{inr(r.auctions)}</td>
                        <td className="num">{inr(r.financiers)}</td>
                        <td className="nad-mut cap" title={r.segments}>{r.segments || "·"}</td>
                        <td className="nad-mut cap" title={r.states}>
                          {r.states || "·"}{r.states_count > 3 ? ` +${r.states_count - 3}` : ""}
                        </td>
                      </tr>
                      {winOpen[i] && (
                        <tr className="nad-win-d">
                          <td colSpan={12}>
                            <div className="nad-wd">
                              <div>
                                <h5>Financiers · {inr(r.financiers)}</h5>
                                <div className="nad-chips">
                                  {(r.financier_list || "").split(", ").filter(Boolean).map((x, j) => (
                                    <span className="nad-chip" key={j}>{x}</span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <h5>Auction ids · {inr(r.auctions)}</h5>
                                <div className="nad-chips">
                                  {(r.auction_ids || []).map((id, j) => (
                                    <span className="nad-chip mono" key={j}>{id}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {!topWin.rows.length && (
                      <tr><td colSpan={12} className="nad-mut" style={{ padding: 20 }}>
                        No winner holds {WIN_MIN} or more assets in this selection.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bids at or above 15L — auction-wise (opened from the KPI card) */}
      {highBids && (
        <div className="nad-ov" onClick={() => setHighBids(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Bids ₹15L and above</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · {highBids.rows.length} asset{highBids.rows.length === 1 ? "" : "s"} across{" "}
                {new Set(highBids.rows.map((r) => r.auction_id)).size} auction
                {new Set(highBids.rows.map((r) => r.auction_id)).size === 1 ? "" : "s"}
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportHighBids}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setHighBids(null)}>Close</button>
            </div>
            {highBids.loading ? <div className="nad-mut" style={{ padding: 16 }}>Loading…</div> : (
              <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
                <table className="nad-tbl">
                  <thead><tr>
                    <th className="frz f1">State</th><th>Segment</th><th>Financier</th>
                    <th>Asset ref no</th><th className="num">Start price</th>
                    <th className="num">RP</th><th className="num">Bid price</th><th>Bidder</th>
                  </tr></thead>
                  <tbody>
                    {highBids.rows.map((r, i) => {
                      const newAuction = i === 0 || highBids.rows[i - 1].auction_id !== r.auction_id;
                      return (
                        <Fragment key={r.asset_id}>
                          {newAuction && (
                            <tr className="nad-gh"><td colSpan={8}>
                              <div className="nad-gh-lead">
                                <b className="nad-gh-state">{r.auction_id}</b>
                                <span className="nad-mut">
                                  {highBids.rows.filter((x) => x.auction_id === r.auction_id).length} asset(s)
                                </span>
                              </div>
                            </td></tr>
                          )}
                          <tr>
                            <td className="frz f1">{r.state}</td>
                            <td><span className="pillseg" style={{ background: SEG_COLOR[r.segment] || "#8592a6" }}>{r.segment}</span></td>
                            <td>{r.financier}</td>
                            <td className="mono">{r.reference_no || "—"}</td>
                            <td className="num">{r.start_price ? cur(r.start_price) : "·"}</td>
                            <td className="num">{r.recommended_bid ? cur(r.recommended_bid) : "·"}</td>
                            <td className="num b good">{cur(r.bid)}</td>
                            <td>{r.bidder_name || "—"}{r.mode === "Offline" ? <span className="tag tag-off" style={{ marginLeft: 6 }}>Offline</span> : null}</td>
                          </tr>
                        </Fragment>
                      );
                    })}
                    {!highBids.rows.length && (
                      <tr><td colSpan={8} className="nad-mut" style={{ padding: 16 }}>No bids at or above ₹15L.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Total bid value by segment (opened from the KPI card) */}
      {segBreak === "value" && (
        <div className="nad-ov" onClick={() => setSegBreak(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Total bid value — by segment</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · Σ highest bid per bidded asset · reflects current filters
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportValueBreak}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setSegBreak(null)}>Close</button>
            </div>
            <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
              <table className="nad-tbl">
                <thead>
                  <tr>
                    <th className="frz f1">Segment</th>
                    <th className="num">Bidded</th><th className="num">Total bid value</th>
                    <th className="num">Avg / asset</th><th className="num">Share of value</th>
                  </tr>
                </thead>
                <tbody>
                  {valueRows.map((r) => (
                    <tr key={r.seg}>
                      <td className="frz f1">
                        <span className="pillseg" style={{ background: SEG_COLOR[r.seg] || "#8592a6" }}>{r.seg}</span>
                      </td>
                      <td className="num">{inr(r.bidded)}</td>
                      <td className="num b good">{cur(r.sum_high)}</td>
                      <td className="num">{r.bidded ? cur(r.sum_high / r.bidded) : "·"}</td>
                      <td className="num">{pct(r.sum_high, grand.sum_high)}%</td>
                    </tr>
                  ))}
                  {valueRows.length > 0 && (
                    <tr className="nad-tot">
                      <td className="frz f1">Total</td>
                      <td className="num">{inr(grand.bidded)}</td>
                      <td className="num">{cur(grand.sum_high)}</td>
                      <td className="num">{grand.bidded ? cur(grand.sum_high / grand.bidded) : "—"}</td>
                      <td className="num">100%</td>
                    </tr>
                  )}
                  {!valueRows.length && (
                    <tr><td colSpan={5} className="nad-mut" style={{ padding: 16 }}>No bid value for this selection.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Meta winning by segment (opened from the KPI card) */}
      {segBreak === "meta" && (
        <div className="nad-ov" onClick={() => setSegBreak(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Meta winning — by segment</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · bidded assets flagged Winning on the meta portal · reflects current filters
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportMetaBreak}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setSegBreak(null)}>Close</button>
            </div>
            <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
              <table className="nad-tbl">
                <thead>
                  <tr>
                    <th className="frz f1">Segment</th>
                    <th className="num">Meta winning</th><th className="num">Meta win value</th>
                    <th className="num">Bidded</th>
                    <th className="num">Share</th><th className="num">Of which &gt;80%</th>
                  </tr>
                </thead>
                <tbody>
                  {metaRows.map((r) => (
                    <tr key={r.seg}>
                      <td className="frz f1">
                        <span className="pillseg" style={{ background: SEG_COLOR[r.seg] || "#8592a6" }}>{r.seg}</span>
                      </td>
                      <td className="num b good">{inr(r.meta_win)}</td>
                      <td className="num b">{r.sum_high_meta ? cur(r.sum_high_meta) : "·"}</td>
                      <td className="num">{inr(r.bidded)}</td>
                      <td className="num">{r.bidded ? pct(r.meta_win, r.bidded) + "%" : "·"}</td>
                      <td className="num">{r.rp80_meta_win ? inr(r.rp80_meta_win) : "·"}</td>
                    </tr>
                  ))}
                  {metaRows.length > 0 && (
                    <tr className="nad-tot">
                      <td className="frz f1">Total</td>
                      <td className="num">{inr(grand.meta_win)}</td>
                      <td className="num">{cur(grand.sum_high_meta)}</td>
                      <td className="num">{inr(grand.bidded)}</td>
                      <td className="num">{grand.bidded ? pct(grand.meta_win, grand.bidded) + "%" : "—"}</td>
                      <td className="num">{inr(grand.rp80_meta_win)}</td>
                    </tr>
                  )}
                  {!metaRows.length && (
                    <tr><td colSpan={6} className="nad-mut" style={{ padding: 16 }}>No meta-winning assets for this selection.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Bid→RP above 80% by segment (opened from the KPI card) */}
      {segBreak === "rp80" && (
        <div className="nad-ov" onClick={() => setSegBreak(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Bid → RP above 80% — by segment</b>
              <span className="nad-mut" style={{ marginLeft: 8 }}>
                · only assets whose own Bid→RP exceeds 80% · reflects current filters
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportSegBreak}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setSegBreak(null)}>Close</button>
            </div>
            <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
              <table className="nad-tbl">
                <thead>
                  <tr>
                    <th className="frz f1">Segment</th>
                    <th className="num">Assets &gt;80%</th><th className="num">Meta win</th>
                    <th className="num">Meta win value</th>
                    <th className="num">Bidded</th><th className="num">Share</th>
                    <th className="num">Sum max bids</th><th className="num">Sum reserve</th><th className="num">Bid→RP</th>
                  </tr>
                </thead>
                <tbody>
                  {rp80Rows.map((r) => {
                    const rp = r.sum_rp_80 ? Math.round((r.sum_high_80 / r.sum_rp_80) * 100) : null;
                    return (
                      <tr key={r.seg}>
                        <td className="frz f1">
                          <span className="pillseg" style={{ background: SEG_COLOR[r.seg] || "#8592a6" }}>{r.seg}</span>
                        </td>
                        <td className="num b good">{inr(r.rp80)}</td>
                        <td className="num">{r.rp80_meta_win ? inr(r.rp80_meta_win) : "·"}</td>
                        <td className="num">{r.sum_high_80_meta ? cur(r.sum_high_80_meta) : "·"}</td>
                        <td className="num">{inr(r.bidded)}</td>
                        <td className="num">{r.bidded ? pct(r.rp80, r.bidded) + "%" : "·"}</td>
                        <td className="num">{r.sum_high_80 ? cur(r.sum_high_80) : "·"}</td>
                        <td className="num">{r.sum_rp_80 ? cur(r.sum_rp_80) : "·"}</td>
                        <td className="num">{rp == null ? "·" : <span className={"rp g-" + band(rp)}>{rp}%</span>}</td>
                      </tr>
                    );
                  })}
                  {rp80Rows.length > 0 && (
                    <tr className="nad-tot">
                      <td className="frz f1">Total</td>
                      <td className="num">{inr(grand.rp80)}</td>
                      <td className="num">{inr(grand.rp80_meta_win)}</td>
                      <td className="num">{cur(grand.sum_high_80_meta)}</td>
                      <td className="num">{inr(grand.bidded)}</td>
                      <td className="num">{grand.bidded ? pct(grand.rp80, grand.bidded) + "%" : "—"}</td>
                      <td className="num">{cur(grand.sum_high_80)}</td>
                      <td className="num">{cur(grand.sum_rp_80)}</td>
                      <td className="num">{grand.sum_rp_80 ? Math.round((grand.sum_high_80 / grand.sum_rp_80) * 100) + "%" : "—"}</td>
                    </tr>
                  )}
                  {!rp80Rows.length && (
                    <tr><td colSpan={9} className="nad-mut" style={{ padding: 16 }}>No assets above 80% for this selection.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* asset detail modal */}
      {detail && (
        <div className="nad-ov" onClick={() => setDetail(null)}>
          <div className="nad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>{detail.title}</b>
              <span className="nad-mut" style={{ marginLeft: 10 }}>
                {detail.seg ? `${inr(detail.seg.bidded)} bidded · Bid→RP ${detail.seg.sum_rp ? Math.round((detail.seg.sum_high_rp / detail.seg.sum_rp) * 100) : "—"}%` : ""}
              </span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportDetail}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setDetail(null)}>Close</button>
            </div>
            {detail.loading ? <div className="nad-mut" style={{ padding: 16 }}>Loading assets…</div> : (
              <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
                <table className="nad-tbl">
                  <thead><tr>
                    <th className="frz f1">Asset</th><th>Reg No</th><th>Mode</th><th>Meta status</th>
                    <th className="num">Reserve</th>
                    <th className="num">Highest bid</th><th className="num">Bid→RP</th><th className="num">Bidders</th><th className="num">Bids</th>
                  </tr></thead>
                  <tbody>
                    {detail.rows.map((r) => (
                      <tr key={r.asset_id}>
                        <td className="frz f1">{r.name}</td>
                        <td className="mono">{r.reg_no || "—"}</td>
                        <td>
                          {r.is_offline
                            ? <span className="tag tag-off">Offline</span>
                            : <span className="tag tag-on">Online</span>}
                        </td>
                        <td>
                          {r.meta_status
                            ? <span className={"tag " + (String(r.meta_status).toLowerCase() === "winning" ? "tag-win" : "tag-lose")}>{r.meta_status}</span>
                            : <span className="nad-mut">—</span>}
                        </td>
                        <td className="num">{r.reserve ? cur(r.reserve) : "·"}</td>
                        <td className="num b">{r.bid ? cur(r.bid) : "·"}</td>
                        <td className="num">{r.bid_to_rp == null ? "—" : <span className={"rp g-" + band(r.bid_to_rp)}>{r.bid_to_rp}%</span>}</td>
                        <td className="num"><span className="nad-bd" onClick={() => openBidders(r.name, { mode: "asset", asset_id: r.asset_id })}>{r.bidders}</span></td>
                        <td className="num">{r.bids}</td>
                      </tr>
                    ))}
                    {detail.seg && detail.rows.length > 0 && (
                      <tr className="nad-tot">
                        <td className="frz f1">Total · {detail.rows.length} assets</td><td />
                        <td>{inr(detail.rows.filter((r) => r.is_offline).length)} offline</td>
                        <td>{inr(detail.rows.filter((r) => String(r.meta_status).toLowerCase() === "winning").length)} winning</td>
                        <td className="num">{cur(detail.seg.sum_rp)}</td>
                        <td className="num">{cur(detail.seg.sum_high)}</td>
                        <td className="num">{detail.seg.sum_rp ? Math.round((detail.seg.sum_high_rp / detail.seg.sum_rp) * 100) + "%" : "—"}</td>
                        <td className="num">{detail.seg.pbidders}</td>
                        <td className="num">{inr(detail.seg.total_bids)}</td>
                      </tr>
                    )}
                    {!detail.rows.length && <tr><td colSpan={9} className="nad-mut" style={{ padding: 16 }}>No bidded assets.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* bidders modal */}
      {bidders && (
        <div className="nad-ov" onClick={() => setBidders(null)}>
          <div className="nad-modal nad-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="nad-mh">
              <b>Unique bidders</b><span className="nad-mut" style={{ marginLeft: 8 }}>· {bidders.title} · {bidders.rows.length}</span>
              <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={exportBidders}>⤓ Excel</button>
              <button className="nad-btn nad-btn-sm" onClick={() => setBidders(null)}>Close</button>
            </div>
            {bidders.loading ? <div className="nad-mut" style={{ padding: 16 }}>Loading…</div> : (
              <div className="nad-scroll" style={{ maxHeight: "62vh" }}>
                {bidders.rows.map((r, i) => (
                  <div className="nad-brow" key={i}>
                    <span className="nad-bidx">{i + 1}</span>
                    <span className="nad-bnm">{r.name}</span>
                    <span className="nad-bab">{r.assets_bid} asset{r.assets_bid === 1 ? "" : "s"}</span>
                    <span className="nad-bph">{r.phone}</span>
                  </div>
                ))}
                {!bidders.rows.length && <div className="nad-mut" style={{ padding: 16 }}>No bidder details.</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Export wizard ────────────────────────────────────────────────────────────
// The board is aggregate; this builds the asset-level download. The server renders
// the .xlsx and streams it, so nothing here holds a big payload in the browser.
const XBLOCKS = [
  { k: "id", t: "Identity", lock: true, n: 3, c: "Auction id, reference no, registration no" },
  { k: "veh", t: "Vehicle", n: 3, c: "Make and model, mfg year, segment" },
  { k: "loc", t: "Location", n: 3, c: "State, yard name, city" },
  { k: "own", t: "Ownership", n: 2, c: "Financier brand, segment owner" },
  { k: "time", t: "Timing", n: 2, c: "Auction start and end date" },
  { k: "price", t: "Pricing", n: 2, c: "Start price, reserve price" },
  { k: "bid", t: "Bidding", n: 4, c: "Bid count, unique bidders, highest bid, online or offline" },
  { k: "out", t: "Outcome", n: 4, liveBlank: true, c: "Status, winner, approval and payment date" },
];
const XLVL = { own: "SH / Owner", st: "State", fin: "Financier", seg: "Segment" };
const XSORT = [["end", "Auction end date"], ["ref", "Reference no"], ["hi", "Highest bid, high to low"], ["sp", "Start price"]];

function ExportWizard({ isLive, F, cells, stateOpts, finOpts, segOpts, ownerOpts, onClose, onRun, busy }) {
  const [sel, setSel] = useState({ state: F.state.slice(), financier: F.financier.slice(), segment: F.segment.slice(), owner: F.owner.slice() });
  const [blocks, setBlocks] = useState(() => new Set(["id", "veh", "loc", "own", "time", "price", "bid"].concat(isLive ? [] : ["out"])));
  const [seq, setSeq] = useState(["own", "st", "seg"]);
  const [splitLevel, setSplit] = useState(0);
  const [sort, setSort] = useState("end");
  const [summary, setSummary] = useState(true);
  const [subtotal, setSubtotal] = useState(true);
  const [onlyBidded, setOnlyBidded] = useState(false);
  const [fullDump, setFullDump] = useState(false);

  // Row/sheet estimates come from board.cells, which the browser already holds — the
  // wizard never queries just to show a count.
  const CK = { state: "st", financier: "fin", segment: "seg", owner: "own" };
  const keep = (c) => Object.keys(CK).every((k) => !sel[k].length || sel[k].includes(c[CK[k]]));
  const picked = (cells || []).filter(keep);
  const rowCount = picked.reduce((n, c) => n + (onlyBidded ? (c.bidded || 0) : (c.assets || 0)), 0);
  const sheetCount = splitLevel
    ? new Set(picked.map((c) => seq.slice(0, splitLevel).map((d) => c[d]).join(" · "))).size || 1
    : 1;
  const colCount = XBLOCKS.reduce((n, b) => n + (blocks.has(b.k) ? b.n : 0), 0);

  const toggleBlock = (k) => setBlocks((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const move = (i, d) => setSeq((s) => { const n = s.slice(); const [x] = n.splice(i, 1); n.splice(i + d, 0, x); return n; });
  const drop = (i) => setSeq((s) => { const n = s.slice(); n.splice(i, 1); setSplit((l) => Math.min(l, n.length)); return n; });

  const run = () => onRun({
    states: sel.state, financiers: sel.financier, segments: sel.segment, owners: sel.owner,
    blocks: [...blocks].join(","), seq: seq.join(","), split_level: String(splitLevel), sort,
    summary: summary ? "1" : "0", subtotal: subtotal ? "1" : "0",
    only_bidded: onlyBidded ? "1" : "0", full_dump: fullDump ? "1" : "0",
  });

  const anyInherited = ["state", "financier", "segment", "owner"].some((k) => F[k].length);
  return (
    <div className="nad-ov" onClick={onClose}>
      <div className="nad-modal nad-wiz" onClick={(e) => e.stopPropagation()}>
        <div className="nad-mh">
          <b>Export wizard</b>
          <span className="nad-mut">· one row per asset · {isLive ? "live auctions" : "completed auctions"}</span>
          <button className="nad-btn nad-btn-sm" style={{ marginLeft: "auto" }} onClick={onClose}>Close</button>
        </div>

        <div className="nad-wz-bd">
          <div className="nad-step">
            <div className="nad-sh"><i>1</i><b>Scope</b><span className="nad-mut">{rowCount ? inr(rowCount) + " assets" : "nothing selected"}</span></div>
            <div className="nad-sb">
              <div className="nad-inherit">
                {anyInherited
                  ? <><b>Inherited from the board.</b> Change it here if the download needs a different cut.</>
                  : <><b>No board filters applied.</b> Pick whatever cut the download needs — it does not have to match the screen.</>}
              </div>
              <div className="nad-filters" style={{ marginBottom: 0 }}>
                <MultiSelect label="State" options={stateOpts} selected={sel.state} onChange={(v) => setSel((s) => ({ ...s, state: v }))} />
                <MultiSelect label="Financier" options={finOpts} selected={sel.financier} onChange={(v) => setSel((s) => ({ ...s, financier: v }))} />
                <MultiSelect label="Segment" options={segOpts} selected={sel.segment} onChange={(v) => setSel((s) => ({ ...s, segment: v }))} />
                <MultiSelect label="SH / Owner" options={ownerOpts} selected={sel.owner} onChange={(v) => setSel((s) => ({ ...s, owner: v }))} />
              </div>
            </div>
          </div>

          <div className="nad-step">
            <div className="nad-sh"><i>2</i><b>Columns</b><span className="nad-mut">{colCount} columns{fullDump ? " + bid sheet" : ""}</span></div>
            <div className="nad-sb">
              <div className="nad-blocks">
                {XBLOCKS.map((b) => {
                  const dead = isLive && b.liveBlank;
                  const on = blocks.has(b.k) && !dead;
                  return (
                    <div key={b.k} className={"nad-blk" + (on ? " on" : "") + (b.lock ? " lock" : "") + (dead ? " dead" : "")}
                      onClick={() => (!b.lock && !dead ? toggleBlock(b.k) : null)}>
                      <div className="nad-blk-t"><span className="nad-box" />{b.t}<i>{b.n} cols{b.lock ? " · always" : ""}</i></div>
                      <div className="nad-blk-c">{b.c}</div>
                      {dead && <div className="nad-blk-w">Auction_summary is written only at close</div>}
                      {isLive && b.k === "bid" && <div className="nad-blk-w ok">live · highest bid from Max(latest_bid)</div>}
                    </div>
                  );
                })}
              </div>
              <div className={"nad-dump" + (fullDump ? " on" : "")} onClick={() => setFullDump((v) => !v)}>
                <span className="nad-sw" />
                <div>
                  <b>Full dump — every bid as its own row</b>
                  <span>Second sheet: bidder, amount, time and channel, one row per bid.
                    {isLive ? " Mid-auction this is competitive information — it follows the viewer's permissions." : ""}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="nad-step">
            <div className="nad-sh"><i>3</i><b>Sheet layout</b>
              <span className="nad-mut">{seq.length ? seq.map((k) => XLVL[k]).join(" → ") : "flat"} · {splitLevel ? sheetCount + " sheets" : "one sheet"}</span></div>
            <div className="nad-sb">
              <div className="nad-grp">
                <h4>Row hierarchy</h4>
                <div className="nad-seq">
                  {!seq.length && <span className="nad-mut">Flat — no grouping, one row per asset</span>}
                  {seq.map((k, i) => (
                    <Fragment key={k}>
                      {i > 0 && <span className="nad-arrow">→</span>}
                      <span className="nad-lvl">
                        <em>{i + 1}</em>{XLVL[k]}
                        <button type="button" disabled={i === 0} onClick={() => move(i, -1)} title="move up">↑</button>
                        <button type="button" disabled={i === seq.length - 1} onClick={() => move(i, 1)} title="move down">↓</button>
                        <button type="button" onClick={() => drop(i)} title="remove">×</button>
                      </span>
                    </Fragment>
                  ))}
                </div>
                <div className="nad-seq">
                  {Object.keys(XLVL).filter((k) => !seq.includes(k)).length > 0 && <span className="nad-mut">add a level:</span>}
                  {Object.keys(XLVL).filter((k) => !seq.includes(k)).map((k) => (
                    <button key={k} type="button" className="nad-add" onClick={() => setSeq((s) => [...s, k])}>+ {XLVL[k]}</button>
                  ))}
                </div>
              </div>
              <div className="nad-grp">
                <h4>Start a new sheet at</h4>
                <div className="nad-rrow">
                  <button type="button" className={"nad-rad" + (splitLevel === 0 ? " on" : "")} onClick={() => setSplit(0)}>One sheet</button>
                  {seq.map((k, i) => (
                    <button key={k} type="button" className={"nad-rad" + (splitLevel === i + 1 ? " on" : "")} onClick={() => setSplit(i + 1)}>
                      Level {i + 1} — {XLVL[k]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="nad-grp">
                <h4>Within the deepest group, sort by</h4>
                <div className="nad-rrow">
                  {XSORT.map(([k, t]) => (
                    <button key={k} type="button" className={"nad-rad" + (sort === k ? " on" : "")} onClick={() => setSort(k)}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="nad-grp">
                <h4>Extras</h4>
                <div className="nad-rrow">
                  <button type="button" className={"nad-rad" + (summary ? " on" : "")} onClick={() => setSummary((v) => !v)}>Summary sheet</button>
                  <button type="button" className={"nad-rad" + (subtotal ? " on" : "")} onClick={() => setSubtotal((v) => !v)}>Subtotal row per group</button>
                  <button type="button" className={"nad-rad" + (onlyBidded ? " on" : "")} onClick={() => setOnlyBidded((v) => !v)}>Only assets that got a bid</button>
                </div>
              </div>
            </div>
          </div>

          <div className="nad-step">
            <div className="nad-sh"><i>4</i><b>Review</b></div>
            <div className="nad-sb">
              <div className="nad-stats">
                <div><u>Asset rows</u><b>{inr(rowCount)}</b></div>
                <div><u>Columns</u><b>{colCount}</b></div>
                <div><u>Sheets</u><b>{sheetCount + (summary && seq.length ? 1 : 0) + (fullDump ? 1 : 0)}</b></div>
                <div><u>Grouping</u><b className="sm">{seq.length ? seq.map((k) => XLVL[k]).join(" → ") : "flat"}</b></div>
              </div>
              {rowCount > 50000 && <div className="nad-warn">Over 50,000 rows — this will take a while to build. Narrowing the scope in step 1 cuts the work proportionally.</div>}
            </div>
          </div>
        </div>

        <div className="nad-wz-ft">
          <button className="nad-btn nad-primary" disabled={busy || !rowCount} onClick={run}>
            {busy ? "Building…" : "Build & download"}
          </button>
          <button className="nad-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <span className="nad-mut">
            {busy ? "The server is writing the workbook — this can take a few seconds."
                  : "Built on the server and streamed as a file."}
          </span>
        </div>
      </div>
    </div>
  );
}

const CSS = `
.nad { font-family: inherit; color: #15191e; }
.nad-top { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap; margin-bottom:12px; }
.nad-title { font-size:1.2rem; font-weight:700; }
.nad-badge { font-size:.62rem; text-transform:uppercase; letter-spacing:.06em; background:#e6f1ea; color:#2f7d51; padding:2px 6px; border-radius:5px; vertical-align:middle; }
.nad-sub { color:#6e7a86; font-size:.82rem; margin-top:2px; }
.nad-ctrls { display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap; }
.nad-btn { font:inherit; font-size:.8rem; border:1px solid #dfe4ea; background:#fff; padding:7px 12px; border-radius:8px; cursor:pointer; }
.nad-btn.nad-primary { background:#2f7d51; color:#fff; border-color:#2f7d51; }
.nad-btn-sm { font-weight:600; font-size:.74rem; padding:5px 10px; }
.nad-filters { display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; margin-bottom:14px; }
.nad-fx { display:flex; flex-direction:column; gap:3px; font-size:.8rem; }
.nad-fx span { font-size:.62rem; text-transform:uppercase; letter-spacing:.05em; color:#6e7a86; font-weight:650; }
.nad-fx input { font:inherit; font-size:.8rem; padding:6px 9px; border:1px solid #dfe4ea; border-radius:8px; background:#fff; }
.nad-clear { align-self:flex-end; background:none; border:0; color:#2f7d51; font-weight:600; cursor:pointer; font-size:.8rem; }
.nad-groupby { display:inline-flex; border:1px solid #dfe4ea; border-radius:8px; overflow:hidden; margin-top:4px; }
.nad-groupby button { border:0; background:#fff; color:#6b7280; padding:5px 12px; font-size:.78rem; font-weight:600; cursor:pointer; border-right:1px solid #dfe4ea; }
.nad-groupby button:last-child { border-right:0; }
.nad-groupby button:hover { background:#f2f5f8; color:#15191e; }
.nad-groupby button.on, .nad-groupby button.on:hover { background:#2f7d51; color:#fff; }
.nad-smart { align-self:flex-end; display:flex; align-items:center; gap:6px; font-size:.78rem; color:#3f4855; cursor:pointer; border:1px solid #dfe4ea; background:#fff; padding:6px 11px; border-radius:8px; }
.nad-smart input:disabled { cursor:not-allowed; }
.nad-ms { position:relative; }
.nad-ms-btn { display:flex; align-items:center; justify-content:space-between; gap:8px; min-width:150px; font:inherit; font-size:.8rem; padding:6px 9px; border:1px solid #dfe4ea; border-radius:8px; background:#fff; cursor:pointer; text-align:left; }
.nad-ms-lbl { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:160px; }
.nad-ms-car { color:#6e7a86; font-size:.7rem; }
.nad-ms-pop { position:absolute; top:100%; left:0; margin-top:4px; z-index:40; background:#fff; border:1px solid #dfe4ea; border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.16); min-width:190px; max-width:280px; }
.nad-ms-actions { display:flex; gap:12px; padding:8px 10px; border-bottom:1px solid #edf0f4; }
.nad-ms-actions button { font:inherit; font-size:.72rem; font-weight:650; color:#2f7d51; background:none; border:0; cursor:pointer; padding:0; }
.nad-ms-list { max-height:min(340px,50vh); overflow-y:auto; overscroll-behavior:contain; padding:4px; }
.nad-ms-opt { display:flex; align-items:center; gap:8px; padding:5px 8px; font-size:.8rem; border-radius:6px; cursor:pointer; white-space:nowrap; }
.nad-ms-opt:hover { background:#f4f6f9; }
.nad-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:8px; margin-bottom:12px; }
@media (min-width:1120px) { .nad-kpis { grid-template-columns:repeat(5,minmax(0,1fr)); } }
.nad-kpi { background:#fff; border:1px solid #e6eaf0; border-radius:10px; padding:9px 11px; min-width:0; }
.nad-kpi.hl { background:#fff; border-color:#cfe0d6; box-shadow:inset 3px 0 0 #2f7d51; }
.nad-kpi-clk { cursor:pointer; transition:box-shadow .12s ease, transform .12s ease; }
.nad-kpi-clk:hover { box-shadow:0 4px 14px rgba(0,0,0,.10); transform:translateY(-1px); }
.nad-kpi-clk:focus-visible { outline:2px solid #2f7d51; outline-offset:2px; }
.nad-kl { font-size:.65rem; color:#6e7a86; font-weight:650; text-transform:uppercase; letter-spacing:.045em; }
.nad-kv { font-size:1.3rem; font-weight:700; margin-top:4px; line-height:1.05; font-variant-numeric:tabular-nums; }
.nad-kv small { font-size:.9rem; color:#6e7a86; }
.nad-kc { font-size:.67rem; color:#6e7a86; margin-top:3px; line-height:1.35;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.nad-card { background:#fff; border:1px solid #e6eaf0; border-radius:12px; margin-bottom:16px; overflow:hidden; }
.nad-ch { padding:13px 16px 6px; font-weight:640; font-size:1rem; }
.nad-ch-row { display:flex; justify-content:space-between; align-items:center; gap:12px; }
.nad-mut { color:#6e7a86; font-size:.72rem; }
.g-good{color:#2f7d51;} .g-mid{color:#b26b1f;} .g-low{color:#b23a2e;}
.nad-scroll { overflow:auto; max-height:66vh; }
.nad-tbl { border-collapse:collapse; width:100%; font-size:.82rem; }
.nad-tbl thead th { position:sticky; top:0; background:#233042; text-align:right; font-size:.64rem; text-transform:uppercase; letter-spacing:.04em; color:#eaf0f6; font-weight:650; padding:9px 10px; white-space:nowrap; z-index:2; }
.nad-tbl thead th.f1, .nad-tbl thead th.f2 { text-align:left; }
.nad-tbl th.num, .nad-tbl td.num { text-align:right; }
.nad-tbl td { padding:7px 10px; border-bottom:1px solid #edf0f4; white-space:nowrap; }
.nad-tbl td.b { font-weight:700; }
.nad-tbl td.good { color:#2f7d51; }
.nad-tbl th.frz, .nad-tbl td.frz { position:sticky; background:#fff; z-index:1; }
.nad-tbl thead th.frz { z-index:3; }
.nad-tbl .f1 { left:0; min-width:150px; max-width:210px; overflow:hidden; text-overflow:ellipsis; font-weight:700; border-right:1px solid #e6eaf0; vertical-align:top; }
.nad-tbl .f2 { left:150px; min-width:120px; }
.pillseg { display:inline-block; font-size:.68rem; font-weight:700; color:#fff; padding:2px 8px; border-radius:5px; }
.nad-view { font-size:.6rem; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:#2f7d51; margin-left:6px; opacity:.7; }
.rp { font-weight:700; padding:1px 6px; border-radius:5px; font-size:.72rem; }
.rp.g-good { background:#e6f1ea; } .rp.g-mid { background:#fdf3e4; } .rp.g-low { background:#fdeeec; }
.nad-bd { color:#2f7d51; font-weight:700; cursor:pointer; border-bottom:1px dotted #2f7d51; }
.nad-bd:hover { opacity:.7; }
.nad-gh > td { background:#f4f6f9; border-top:2px solid #dfe4ea; padding:9px 12px; cursor:pointer; }
.nad-gh-in { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.nad-gh-lead { display:flex; align-items:center; gap:9px; min-width:240px; }
.nad-gh-state { font-size:.95rem; }
.nad-caret { color:#6e7a86; margin-right:5px; }
.nad-dl-sm { font:inherit; font-size:.7rem; border:1px solid #dfe4ea; background:#fff; border-radius:6px; padding:1px 7px; cursor:pointer; color:#2f7d51; }
.nad-gh-metrics { display:flex; align-items:stretch; gap:5px; flex-wrap:wrap; margin-left:auto; }
.nad-chips { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }
.nad-chip { display:inline-flex; align-items:baseline; gap:5px; border:1px solid #dfe4ea;
  border-radius:999px; padding:2px 9px; font-size:.72rem; background:#fafbfd; }
.nad-chip u { text-decoration:none; font-weight:700; font-size:.65rem; letter-spacing:.03em; color:#2f7d51; }
.nad-chip b { font-weight:700; font-variant-numeric:tabular-nums; }
.nad-chip s { text-decoration:none; color:#6e7a86; font-size:.67rem; font-variant-numeric:tabular-nums; }
.nad-gi { display:inline-flex; flex-direction:column; align-items:flex-start; gap:0;
  background:#fff; border:1px solid #e6eaf0; border-radius:6px; padding:3px 8px; min-width:0; }
.nad-gi i { font-style:normal; font-size:.58rem; text-transform:uppercase; letter-spacing:.045em; color:#6e7a86; font-weight:700; white-space:nowrap; }
.nad-gi b { font-size:.85rem; color:#15191e; line-height:1.25; font-variant-numeric:tabular-nums; }
.nad-asp { display:flex; align-items:center; gap:6px 12px; flex-wrap:wrap; margin-top:8px; padding-left:22px; font-size:.72rem; color:#6e7a86; }
.nad-asp-l { font-weight:700; text-transform:uppercase; letter-spacing:.03em; font-size:.62rem; }
.nad-asp-i { display:inline-flex; align-items:center; gap:5px; }
.nad-asp-i i { width:9px; height:9px; border-radius:2px; display:inline-block; }
.nad-asp-i b { color:#15191e; }
.nad-row { cursor:pointer; }
.nad-row:hover td:not(.frz) { background:#f7f9fc; }
.nad-tot td { background:#f1f5f9; font-weight:700; border-top:1px solid #dfe4ea; }
.tag { display:inline-block; font-size:.66rem; font-weight:700; padding:2px 7px; border-radius:5px; letter-spacing:.02em; }
.tag-on { background:#eef2f7; color:#3f4855; }
.tag-off { background:#fdf3e4; color:#b26b1f; }
.tag-win { background:#e6f1ea; color:#2f7d51; }
.tag-lose { background:#fdeeec; color:#b23a2e; }
.mono { font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.73rem; color:#3f4855; }
.nad-ov { position:fixed; inset:0; background:rgba(8,12,18,.5); z-index:1200; display:flex; align-items:flex-start; justify-content:center; padding:40px 16px; overflow:auto; }
.nad-modal { background:#fff; border:1px solid #e6eaf0; border-radius:12px; width:min(900px,96vw); box-shadow:0 20px 60px rgba(0,0,0,.35); overflow:hidden; }
.nad-modal-sm { width:min(460px,96vw); }
.nad-modal-wide { width:min(1330px,97vw); }
/* The board table is numeric, so its headers right-align by default; this one is mostly
   text, so headers follow their cells. */
.nad-wtbl thead th { text-align:left; }
.nad-wtbl thead th.num { text-align:right; }
.nad-ob-ctl { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:10px 14px; border-bottom:1px solid #edf0f4; background:#f7f9fc; }
.nad-ob-ctl .nad-lbl { font-size:.62rem; text-transform:uppercase; letter-spacing:.055em; color:#6e7a86; font-weight:700; }
.nad-win-r { cursor:pointer; }
.nad-wtbl td.cap { max-width:210px; overflow:hidden; text-overflow:ellipsis; }
/* Table cells are nowrap, which makes this one demand its full unwrapped width and push
   the whole table sideways — the chip lists must be allowed to wrap. */
.nad-win-d > td { background:#f7f9fc; padding:10px 12px 12px 26px !important; white-space:normal; }
.nad-wd { display:flex; gap:22px; flex-wrap:wrap; }
.nad-wd > div { min-width:0; flex:1 1 340px; max-width:100%; }
.nad-wd h5 { margin:0 0 6px; font-size:.62rem; text-transform:uppercase; letter-spacing:.055em; color:#6e7a86; font-weight:700; }
/* A heavy winner can hold 90+ auctions; cap the list and let it scroll rather than
   pushing every other row off the screen. */
.nad-wd .nad-chips { max-height:124px; overflow-y:auto; align-content:flex-start; margin-top:0; padding-right:4px; }
.nad-wd .nad-chip { white-space:nowrap; }
.nad-mh { display:flex; align-items:center; gap:8px; padding:13px 16px; border-bottom:1px solid #e6eaf0; background:#f4f6f9; }
.nad-brow { display:flex; align-items:center; gap:10px; padding:9px 16px; border-bottom:1px solid #edf0f4; font-size:.84rem; }
.nad-bidx { color:#6e7a86; font-size:.72rem; min-width:20px; }
.nad-bnm { font-weight:600; }
.nad-bab { color:#2f7d51; font-weight:650; font-size:.78rem; }
.nad-bph { margin-left:auto; color:#3f4855; }

/* export split button + menu */
.nad-xw { position:relative; }
.nad-xb { display:flex; border:1px solid #2f7d51; border-radius:8px; overflow:hidden; background:#2f7d51; }
.nad-xb button { font:inherit; font-size:.8rem; font-weight:600; border:0; background:none; color:#fff; padding:7px 12px; cursor:pointer; }
.nad-xb button:hover { background:rgba(0,0,0,.12); }
.nad-xb .car { border-left:1px solid rgba(255,255,255,.3); padding:7px 9px; font-size:.65rem; }
.nad-xmenu { position:absolute; right:0; top:calc(100% + 6px); z-index:45; width:320px; max-width:min(320px,calc(100vw - 32px));
  background:#fff; border:1px solid #dfe4ea; border-radius:10px; box-shadow:0 10px 30px rgba(0,0,0,.14); overflow:hidden; }
.nad-xmi { display:block; width:100%; text-align:left; font:inherit; border:0; border-bottom:1px solid #edf0f4; background:none; padding:10px 13px; cursor:pointer; }
.nad-xmi:last-child { border-bottom:0; }
.nad-xmi:hover { background:#f4f6f9; }
.nad-xmi b { display:block; font-size:.82rem; color:#15191e; margin-bottom:2px; }
.nad-xmi span { display:block; font-size:.73rem; color:#6e7a86; line-height:1.4; }
.nad-xmi em { font-style:normal; font-size:.6rem; text-transform:uppercase; letter-spacing:.04em; font-weight:700;
  padding:1px 5px; border-radius:4px; background:#e6f1ea; color:#2f7d51; margin-left:6px; vertical-align:1px; }
.nad-xmi em.a { background:#fdf3e4; color:#b26b1f; }

/* wizard */
.nad-wiz { max-width:940px; width:100%; overflow:visible; }
.nad-wiz .nad-mh { border-radius:11px 11px 0 0; }
.nad-wiz .nad-wz-ft { border-radius:0 0 11px 11px; }
.nad-wz-bd { padding:14px; background:#f7f9fc; }
.nad-wz-ft { display:flex; gap:9px; align-items:center; flex-wrap:wrap; padding:12px 15px; border-top:1px solid #e6eaf0; background:#fff; }
.nad-step { background:#fff; border:1px solid #e6eaf0; border-radius:10px; margin-bottom:11px; }
.nad-step:last-child { margin-bottom:0; }
.nad-sh { display:flex; align-items:center; gap:9px; padding:10px 13px; border-bottom:1px solid #edf0f4;
  background:#f4f6f9; border-radius:9px 9px 0 0; }
.nad-sh i { font-style:normal; flex:none; width:19px; height:19px; border-radius:50%; background:#2f7d51; color:#fff;
  font-size:.66rem; font-weight:700; display:grid; place-items:center; }
.nad-sh b { font-size:.9rem; }
.nad-sh span { margin-left:auto; text-align:right; }
.nad-sb { padding:13px; }
.nad-inherit { font-size:.78rem; color:#3f4855; background:#f1f5f9; border:1px solid #dfe4ea; border-radius:8px; padding:8px 10px; margin-bottom:11px; }
.nad-wiz .nad-ms-pop { z-index:60; }
.nad-grp { margin-bottom:14px; }
.nad-grp:last-child { margin-bottom:0; }
.nad-grp h4 { margin:0 0 6px; font-size:.65rem; text-transform:uppercase; letter-spacing:.055em; color:#6e7a86; font-weight:700; }
.nad-rrow { display:flex; flex-wrap:wrap; gap:6px; }
.nad-rad { font:inherit; font-size:.78rem; border:1px solid #dfe4ea; background:#fff; color:#3f4855; border-radius:7px; padding:5px 11px; cursor:pointer; }
.nad-rad:hover { border-color:#2f7d51; }
.nad-rad.on { background:#e6f1ea; border-color:#2f7d51; color:#2f7d51; font-weight:650; }
.nad-blocks { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:8px; }
.nad-blk { border:1px solid #dfe4ea; border-radius:8px; padding:9px 10px; cursor:pointer; background:#fff; }
.nad-blk:hover { border-color:#2f7d51; }
.nad-blk.on { border-color:#2f7d51; background:#e6f1ea; }
.nad-blk.lock { border-style:dashed; cursor:default; }
.nad-blk.dead { opacity:.5; border-style:dashed; cursor:not-allowed; }
.nad-blk.dead:hover { border-color:#dfe4ea; }
.nad-blk-t { display:flex; align-items:center; gap:7px; font-size:.8rem; font-weight:650; margin-bottom:3px; }
.nad-blk-t i { margin-left:auto; font-style:normal; font-size:.64rem; font-weight:500; color:#6e7a86; }
.nad-box { flex:none; width:13px; height:13px; border-radius:3px; border:1.5px solid #dfe4ea; background:#fff; position:relative; }
.nad-blk.on .nad-box { background:#2f7d51; border-color:#2f7d51; }
.nad-blk.on .nad-box::after { content:""; position:absolute; left:3.2px; top:.5px; width:3.2px; height:6.6px;
  border:solid #fff; border-width:0 1.6px 1.6px 0; transform:rotate(43deg); }
.nad-blk-c { font-size:.7rem; color:#6e7a86; line-height:1.4; }
.nad-blk-w { font-size:.66rem; color:#b26b1f; font-weight:650; margin-top:4px; }
.nad-blk-w.ok { color:#2f7d51; }
.nad-dump { display:flex; gap:10px; align-items:flex-start; border:1px solid #dfe4ea; border-radius:8px; padding:10px 11px; margin-top:10px; cursor:pointer; background:#fff; }
.nad-dump.on { border-color:#b26b1f; background:#fdf3e4; }
.nad-dump b { display:block; font-size:.8rem; margin-bottom:2px; }
.nad-dump span { display:block; font-size:.72rem; color:#6e7a86; }
.nad-sw { flex:none; width:31px; height:17px; border-radius:999px; background:#dfe4ea; position:relative; margin-top:2px; transition:background .14s; }
.nad-dump.on .nad-sw { background:#b26b1f; }
.nad-sw::after { content:""; position:absolute; top:2px; left:2px; width:13px; height:13px; border-radius:50%; background:#fff; transition:left .14s; }
.nad-dump.on .nad-sw::after { left:16px; }
.nad-seq { display:flex; flex-wrap:wrap; align-items:center; gap:6px; margin-bottom:7px; }
.nad-lvl { display:inline-flex; align-items:center; gap:6px; background:#e6f1ea; border:1px solid #2f7d51; color:#2f7d51;
  border-radius:7px; padding:3px 6px 3px 9px; font-size:.78rem; font-weight:650; }
.nad-lvl em { font-style:normal; font-size:.6rem; opacity:.65; font-family:ui-monospace,Menlo,monospace; }
.nad-lvl button { font:inherit; border:0; background:none; color:inherit; cursor:pointer; padding:0 2px; font-size:.7rem; line-height:1; opacity:.6; }
.nad-lvl button:hover { opacity:1; }
.nad-lvl button:disabled { opacity:.18; cursor:default; }
.nad-arrow { color:#6e7a86; font-size:.75rem; }
.nad-add { font:inherit; font-size:.76rem; border:1px dashed #dfe4ea; background:none; color:#6e7a86; border-radius:7px; padding:3px 9px; cursor:pointer; }
.nad-add:hover { border-style:solid; border-color:#2f7d51; color:#2f7d51; }
.nad-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(120px,1fr)); gap:8px; }
.nad-stats > div { border:1px solid #e6eaf0; border-radius:8px; padding:8px 10px; }
.nad-stats u { display:block; text-decoration:none; font-size:.62rem; text-transform:uppercase; letter-spacing:.05em; color:#6e7a86; font-weight:700; margin-bottom:2px; }
.nad-stats b { font-size:1.15rem; font-weight:700; line-height:1.1; }
.nad-stats b.sm { font-size:.8rem; font-weight:650; }
.nad-warn { margin-top:11px; font-size:.75rem; color:#b26b1f; background:#fdf3e4; border:1px solid #f0dcb8; border-radius:8px; padding:8px 10px; }
`;
